import React, { useMemo, useState } from 'react';
import TiptapEditor from './TiptapEditor';
import pb from '../lib/pocketbase';
import { compressImage } from '../utils/imageCompression';

// DnD Imports
import { DndContext, closestCenter, KeyboardSensor, PointerSensor, useSensor, useSensors } from '@dnd-kit/core';
import { arrayMove, SortableContext, sortableKeyboardCoordinates, verticalListSortingStrategy, useSortable } from '@dnd-kit/sortable';
import { CSS } from '@dnd-kit/utilities';
import {
    FiPlus, FiTrash2, FiEdit2, FiFolder, FiFacebook, FiTwitter, FiInstagram, FiLinkedin, FiGithub, FiGlobe, FiX
} from 'react-icons/fi';

function SortableBlockItem({
    block,
    index,
    updateBlock,
    deleteBlock,
    updateGridColumn,
    updateGridConfig,
    BlockListEditorComponent, // Pass the component to handle recursion
    availableWidgets,
    refreshWidgets,
    onOpenLibrary,
    duplicateBlock
}) {
    const [showHelp, setShowHelp] = useState(false);
    const [showCreateModal, setShowCreateModal] = useState(false);
    const [newWidgetTitle, setNewWidgetTitle] = useState('');
    const [newWidgetCode, setNewWidgetCode] = useState('');
    const [isSaving, setIsSaving] = useState(false);
    const [isFullscreen, setIsFullscreen] = useState(false);
    const [isCustomFullscreen, setIsCustomFullscreen] = useState(false);

    const [editingWidgetId, setEditingWidgetId] = useState(null);
    const [isHtmlFullscreen, setIsHtmlFullscreen] = useState(false);
    const [toast, setToast] = useState({ show: false, message: '' });

    const handleSaveToLibrary = async (title = newWidgetTitle, code = newWidgetCode, type = 'react') => {
        const targetTitle = (typeof title === 'string' && title.length > 0) ? title : (newWidgetTitle || (type === 'html' ? 'Nouveau Composant HTML' : 'Nouveau Widget'));

        if (!targetTitle && !editingWidgetId) {
            alert("Veuillez donner un titre à votre composant.");
            return;
        }

        setIsSaving(true);
        try {
            const data = {
                title: targetTitle,
                type: type,
                code: code || '',
                css: '',
                config: {}
            };

            if (editingWidgetId) {
                await pb.collection('custom_widgets').update(editingWidgetId, data);
            } else {
                await pb.collection('custom_widgets').create(data);
            }

            await refreshWidgets();
            setShowCreateModal(false);
            setEditingWidgetId(null);
            setNewWidgetTitle('');
            setNewWidgetCode('');

            setToast({ show: true, message: "Enregistré avec succès dans la bibliothèque !" });
            setTimeout(() => setToast({ show: false, message: '' }), 3000);
        } catch (err) {
            console.error("Failed to save widget:", err);
            alert("Erreur lors de l'enregistrement : " + err.message);
        } finally {
            setIsSaving(false);
        }
    };

    const handleEditLibraryWidget = () => {
        if (!block.widgetId) return;
        const widget = availableWidgets.find(w => w.id === block.widgetId);
        if (!widget) return;

        setNewWidgetTitle(widget.title);
        setNewWidgetCode(widget.code);
        setEditingWidgetId(widget.id);
        setShowCreateModal(true);
    };

    const handleDeleteLibraryWidget = async () => {
        if (!block.widgetId) return;
        if (!window.confirm("Voulez-vous vraiment supprimer ce composant de la bibliothèque ? Cela affectera TOUTES les pages l'utilisant.")) return;

        try {
            await pb.collection('custom_widgets').delete(block.widgetId);
            await refreshWidgets();
            updateBlock(index, { widgetId: '' });
            alert("Composant supprimé de la bibliothèque.");
        } catch (err) {
            alert("Erreur suppression : " + err.message);
        }
    };

    const handleDetachWidget = () => {
        if (!block.widgetId) return;
        const widget = availableWidgets.find(w => w.id === block.widgetId);
        if (!widget) return;

        if (window.confirm("Détacher ce widget ? Il deviendra une copie unique modifiable sans affecter la bibliothèque.")) {
            updateBlock(index, {
                mode: 'custom',
                customCode: widget.code,
                widgetId: ''
            });
        }
    };
    const widgetHelp = (
        <div style={{ padding: '2.5rem', lineHeight: '1.6', position: 'relative', background: 'var(--admin-card-bg)', borderRadius: '24px', maxWidth: '900px', width: '90%', margin: '40px auto', boxShadow: '0 25px 60px rgba(0,0,0,0.4)', border: '1px solid var(--admin-border)', maxHeight: '90vh', overflowY: 'auto' }}>
            {/* Close Cross */}
            <button
                type="button"
                onClick={() => setShowHelp(false)}
                style={{ position: 'absolute', top: '20px', right: '20px', background: 'rgba(0,0,0,0.1)', border: 'none', borderRadius: '50%', width: '36px', height: '36px', cursor: 'pointer', fontSize: '20px', display: 'grid', placeItems: 'center', transition: 'all 0.2s' }}
                onMouseOver={(e) => e.target.style.background = 'var(--status-danger)'}
                onMouseOut={(e) => e.target.style.background = 'rgba(0,0,0,0.1)'}
            >
                ✕
            </button>

            <div style={{ display: 'flex', alignItems: 'center', gap: '1.5rem', marginBottom: '2rem', borderBottom: '2px solid var(--admin-border)', paddingBottom: '1.5rem' }}>
                <span style={{ fontSize: '3.5rem' }}>🔌</span>
                <div>
                    <h2 style={{ margin: 0, color: 'var(--admin-primary)', fontSize: '1.8rem' }}>Moteur Dynamique Versionnet</h2>
                    <p style={{ margin: 0, opacity: 0.7, fontSize: '1rem', fontWeight: '500' }}>Documentation de Développement & Rendu Temps Réel</p>
                </div>
            </div>

            <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: '2rem', marginBottom: '2.5rem' }}>
                <div style={{ background: 'rgba(99, 102, 241, 0.07)', padding: '1.5rem', borderRadius: '20px', border: '1px solid rgba(99, 102, 241, 0.2)' }}>
                    <h4 style={{ marginTop: 0, fontSize: '1.1rem', color: '#6366f1' }}>� Bibliothèque (Assets)</h4>
                    <p style={{ fontSize: '0.9rem', marginBottom: '1rem' }}>Stockez vos composants critiques dans la collection <code>custom_widgets</code>.</p>
                    <ul style={{ fontSize: '0.85rem', paddingLeft: '1.2rem', color: 'var(--text-muted)' }}>
                        <li><b>Centralisation :</b> Une modification dans la bibliothèque met à jour tout le site.</li>
                        <li><b>CSS Scoping :</b> Le code CSS est injecté proprement dans le head.</li>
                        <li><b>Performance :</b> Chargement asynchrone pour un impact minime.</li>
                    </ul>
                </div>
                <div style={{ background: 'rgba(16, 185, 129, 0.07)', padding: '1.5rem', borderRadius: '20px', border: '1px solid rgba(16, 185, 129, 0.2)' }}>
                    <h4 style={{ marginTop: 0, fontSize: '1.1rem', color: '#10b981' }}>✍️ Live Editor (JSX)</h4>
                    <p style={{ fontSize: '0.9rem', marginBottom: '1rem' }}>Testez instantanément des idées ou créez des widgets localisés.</p>
                    <ul style={{ fontSize: '0.85rem', paddingLeft: '1.2rem', color: 'var(--text-muted)' }}>
                        <li><b>Babel Runtime :</b> Support des dernières syntaxes ES6+ et React.</li>
                        <li><b>Live Compilation :</b> Rendu immédiat lors de la modification.</li>
                        <li><b>Flexibilité :</b> Idéal pour les landing pages urgentes.</li>
                    </ul>
                </div>
            </div>

            <div style={{ marginBottom: '2.5rem' }}>
                <h4 style={{ borderLeft: '5px solid var(--admin-primary)', paddingLeft: '1.2rem', marginBottom: '1.2rem', fontSize: '1.2rem' }}>⚙️ Intégration Contextuelle</h4>
                <div style={{ background: '#1e1e1e', padding: '1.5rem', borderRadius: '16px', border: '1px solid #333' }}>
                    <p style={{ color: '#6a9955', marginBottom: '0.8rem', fontSize: '0.85rem' }}>// Accès direct aux hooks et constantes globales :</p>
                    <code style={{ fontSize: '0.9rem', color: '#d4d4d4' }}>
                        const {'{ theme, useState, useEffect, useRef }'} = window;<br /><br />
                        <span style={{ color: '#569cd6' }}>if</span> (theme === <span style={{ color: '#ce9178' }}>'dark'</span>) {'{'} <span style={{ color: '#6a9955' }}>/* Adapt logic */</span> {'}'}
                    </code>
                </div>
            </div>

            <div style={{ marginBottom: '2.5rem' }}>
                <h4 style={{ borderLeft: '5px solid #f59e0b', paddingLeft: '1.2rem', marginBottom: '1.2rem', fontSize: '1.2rem' }}>🚀 Prototype Rapide : Feedback</h4>
                <div style={{ background: '#1e1e1e', padding: '1.5rem', borderRadius: '16px', border: '1px solid #333', color: '#d4d4d4', fontSize: '0.85rem', fontFamily: 'monospace', marginBottom: '1rem' }}>
                    <div style={{ color: '#569cd6' }}>const</div> <span style={{ color: '#dcdcaa' }}>LikeButton</span> = () ={'>'} {'{'} <br />
                    &nbsp;&nbsp;<span style={{ color: '#569cd6' }}>const</span> [likes, setLikes] = <span style={{ color: '#dcdcaa' }}>useState</span>(<span style={{ color: '#b5cea8' }}>0</span>);<br />
                    &nbsp;&nbsp;<span style={{ color: '#c586c0' }}>return</span> (<br />
                    &nbsp;&nbsp;&nbsp;&nbsp;{'<button onClick={() => setLikes(likes + 1)} style={{padding: "1rem 2rem", borderRadius: "12px", border: "none", cursor: "pointer", background: theme === "dark" ? "#6366f1" : "#1e293b", color: "#fff"}}>'} <br />
                    &nbsp;&nbsp;&nbsp;&nbsp;&nbsp;&nbsp;🔥 Merci ! {'{likes}'} <br />
                    &nbsp;&nbsp;&nbsp;&nbsp;{'</button>'}<br />
                    &nbsp;&nbsp;);<br />
                    {'};'}<br />
                    <span style={{ color: '#dcdcaa', fontWeight: 'bold' }}>render</span>(<span style={{ color: '#808080' }}>{'<LikeButton />'}</span>);
                </div>
            </div>

            <div style={{ marginBottom: '2.5rem' }}>
                <h4 style={{ borderLeft: '5px solid #ec4899', paddingLeft: '1.2rem', marginBottom: '1.2rem', fontSize: '1.2rem' }}>🎨 Exemple Avancé : Dynamic Vibe</h4>
                <div style={{ background: '#1e1e1e', padding: '1.5rem', borderRadius: '16px', border: '1px solid #333', color: '#d4d4d4', fontSize: '0.85rem', fontFamily: 'monospace', overflowX: 'auto' }}>
                    <span style={{ color: '#569cd6' }}>const</span> <span style={{ color: '#dcdcaa' }}>DynamicCard</span> = () ={'>'} {'{'} <br />
                    &nbsp;&nbsp;<span style={{ color: '#569cd6' }}>const</span> isDark = theme === <span style={{ color: '#ce9178' }}>'dark'</span>;<br />
                    &nbsp;&nbsp;<span style={{ color: '#569cd6' }}>const</span> styles = {'{'}<br />
                    &nbsp;&nbsp;&nbsp;&nbsp;container: {'{'} <span style={{ color: '#9cdcfe' }}>padding</span>: <span style={{ color: '#ce9178' }}>'40px'</span>, <span style={{ color: '#9cdcfe' }}>borderRadius</span>: <span style={{ color: '#ce9178' }}>'32px'</span>, <span style={{ color: '#9cdcfe' }}>backgroundColor</span>: isDark ? <span style={{ color: '#ce9178' }}>'#0f172a'</span> : <span style={{ color: '#ce9178' }}>'#fff'</span> {'}'}<br />
                    &nbsp;&nbsp;{'}'};<br />
                    &nbsp;&nbsp;<span style={{ color: '#c586c0' }}>return</span> (<br />
                    &nbsp;&nbsp;&nbsp;&nbsp;{'<div style={styles.container}>'}<br />
                    &nbsp;&nbsp;&nbsp;&nbsp;&nbsp;&nbsp;{'<div style={{fontSize: "4rem"}}>{isDark ? "🌌" : "☀️"}</div>'}<br />
                    &nbsp;&nbsp;&nbsp;&nbsp;&nbsp;&nbsp;{'<h3 style={{color: isDark ? "#fff" : "#000"}}>Mode {theme}</h3>'}<br />
                    &nbsp;&nbsp;&nbsp;&nbsp;{'</div>'}<br />
                    &nbsp;&nbsp;);<br />
                    {'};'}<br />
                    <span style={{ color: '#dcdcaa' }}>render</span>(<span style={{ color: '#808080' }}>{'<DynamicCard />'}</span>);
                </div>
            </div>

            <div style={{ background: 'linear-gradient(to right, #fffbeb, #fef3c7)', border: '1px solid #fde68a', padding: '1.5rem', borderRadius: '16px', display: 'flex', gap: '1rem', alignItems: 'center' }}>
                <span style={{ fontSize: '2rem' }}>🛡️</span>
                <p style={{ margin: 0, fontSize: '0.9rem', color: '#92400e', fontWeight: '500' }}>
                    <b>Sécurité Intégrée :</b> Le moteur utilise des Error Boundaries. Si votre code contient une erreur, seul le widget affichera un message d'erreur. Le reste du site restera fonctionnel.
                </p>
            </div>

            <div style={{ marginTop: '2.5rem', textAlign: 'center' }}>
                <button type="button" className="btn btn-primary" style={{ padding: '1.2rem 3rem', borderRadius: '14px', fontSize: '1.1rem', boxShadow: '0 10px 20px rgba(99, 102, 241, 0.3)' }} onClick={() => setShowHelp(false)}>
                    Fermer le guide
                </button>
            </div>
        </div>
    );
    const [uploading, setUploading] = useState(false);
    const { attributes, listeners, setNodeRef, transform, transition, isDragging } = useSortable({ id: block.id });

    const handleFileChange = async (e) => {
        const file = e.target.files?.[0];
        if (!file) return;

        setUploading(true);
        try {
            const compressed = await compressImage(file, { maxWidth: 1200, quality: 0.8 });
            const fd = new FormData();
            fd.append('file', compressed);

            // On suppose qu'une collection 'media' existe. 
            // Si elle n'existe pas, l'utilisateur devra la créer.
            const res = await pb.collection('media').create(fd);
            const url = pb.files.getUrl(res, res.file);

            updateBlock(index, { url });
        } catch (err) {
            console.error("Upload failed", err);
            alert("Erreur lors de l'upload de l'image. Assurez-vous d'avoir créé une collection nommée 'media' dans PocketBase avec un champ Fichier nommé 'file'.");
        } finally {
            setUploading(false);
        }
    };

    const style = {
        transform: CSS.Transform.toString(transform),
        transition,
    };

    const icons = {
        text: "📝",
        grid: "🏗️",
        button: "🔘",
        image: "🖼️",
        accordion: "🔽",
        testimonial: "⭐",
        list: "📊",
        callout: "🔔",
        video: "🎥",
        timeline: "🛣️",
        stats: "🔢",
        perks: "🛡️",
        downloads: "📂",
        team: "👥",
        map: "🗺️",
        stack: "💻",
        spacer: "↔️",
        gallery: "🏢",
        highlight: "🚨",
        sidebyside: "💎",
        tabs: "🗂️",
        beforeafter: "🔀",
        countdown: "⏳",
        marquee: "🛡️",
        tooltip: "💡",
        codesnippet: "💻",
        awards: "🏆",
        widget: "🔌",
        html: "🌐"
    };

    const labels = {
        text: "Paragraphe",
        grid: "Grille de colonnes",
        button: "Bouton d'action",
        image: "Image illustratrice",
        accordion: "Accordéon / FAQ",
        testimonial: "Témoignage",
        list: "Liste stylisée",
        callout: "Note d'information",
        video: "Vidéo (YouTube/Vimeo)",
        timeline: "Processus de Recrutement",
        stats: "Chiffres Clés / Stats",
        perks: "Avantages & Perks",
        downloads: "Fichiers / Téléchargements",
        team: "Membres de l'équipe",
        map: "Carte & Localisation",
        stack: "Stack Technique / Badges",
        spacer: "Espaceur / Séparateur",
        gallery: "Galerie Photos",
        highlight: "Bannière Highlight",
        sidebyside: "Média + Texte (50/50)",
        tabs: "Onglets Interactifs",
        beforeafter: "Slider Avant / Après",
        countdown: "Compte à Rebours",
        marquee: "Défilement de Logos",
        tooltip: "Info-bulle / Définition",
        codesnippet: "Extrait de Code (Tech)",
        awards: "Récompenses & Certifs",
        widget: "Widget Dynamique (React/Low-Code)",
        html: "Code HTML / Embed (Statique)"
    };

    return (
        <div ref={setNodeRef} style={style} className={`block-card ${isDragging ? 'is-dragging' : ''}`}>
            {/* CARD HEADER */}
            <div className="block-card-header">
                <div className="block-type-badge">
                    <div {...attributes} {...listeners} className="drag-handle" style={{ cursor: 'grab', touchAction: 'none', display: 'flex' }}>
                        <svg width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2"><circle cx="9" cy="5" r="1" /><circle cx="9" cy="12" r="1" /><circle cx="9" cy="19" r="1" /><circle cx="15" cy="5" r="1" /><circle cx="15" cy="12" r="1" /><circle cx="15" cy="19" r="1" /></svg>
                    </div>
                    <span>{icons[block.type]} {labels[block.type]}</span>
                </div>
                <div style={{ display: 'flex', gap: '0.5rem' }}>
                    <button type="button" className="btn-icon" onClick={() => duplicateBlock(index)} title="Dupliquer ce bloc" style={{ color: 'var(--admin-primary)' }}>
                        <svg width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2"><rect x="9" y="9" width="13" height="13" rx="2" ry="2" /><path d="M5 15H4a2 2 0 0 1-2-2V4a2 2 0 0 1 2-2h9a2 2 0 0 1 2 2v1" /></svg>
                    </button>
                    <button type="button" className="btn-icon delete" onClick={() => deleteBlock(index)} title="Supprimer ce bloc">
                        <svg width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"><polyline points="3 6 5 6 21 6"></polyline><path d="M19 6v14a2 2 0 0 1-2 2H7a2 2 0 0 1-2-2V6m3 0V4a2 2 0 0 1 2-2h4a2 2 0 0 1 2 2v2"></path><line x1="10" y1="11" x2="10" y2="17"></line><line x1="14" y1="11" x2="14" y2="17"></line></svg>
                    </button>
                </div>
            </div>

            {/* CARD BODY */}
            <div className="block-card-body">
                {block.type === 'text' && (
                    <TiptapEditor content={block.content} onChange={val => updateBlock(index, { content: val })} />
                )}

                {block.type === 'grid' && (
                    <div>
                        <div className="admin-form-row" style={{ alignItems: 'center' }}>
                            <div className="admin-input-group">
                                <label>Configuration des colonnes</label>
                                <select
                                    style={{ width: '200px' }}
                                    value={block.columnsCount || 2}
                                    onChange={e => updateGridConfig(index, parseInt(e.target.value))}
                                >
                                    {[1, 2, 3, 4].map(n => <option key={n} value={n}>{n} Colonne{n > 1 ? 's' : ''}</option>)}
                                </select>
                            </div>
                        </div>
                        <div className="admin-grid-layout" style={{ gridTemplateColumns: `repeat(${block.columnsCount || 2}, 1fr)` }}>
                            {(block.columns || []).map((colBlocks, colIdx) => (
                                <div key={colIdx} className="admin-grid-col">
                                    <div className="col-header">Colonne {colIdx + 1}</div>
                                    <BlockListEditorComponent blocks={colBlocks || []} onChange={newColBlocks => updateGridColumn(index, colIdx, newColBlocks)} variant="nested" />
                                </div>
                            ))}
                        </div>
                    </div>
                )}

                {block.type === 'button' && (
                    <div className="admin-form-row">
                        <div className="admin-input-group" style={{ flex: 2 }}>
                            <label>Libellé du bouton</label>
                            <input placeholder="Ex: Contactez-nous" value={block.label || ''} onChange={e => updateBlock(index, { label: e.target.value })} />
                        </div>
                        <div className="admin-input-group" style={{ flex: 3 }}>
                            <label>Lien (URL ou Page)</label>
                            <input placeholder="Ex: /contact ou https://..." value={block.url || ''} onChange={e => updateBlock(index, { url: e.target.value })} />
                        </div>
                        <div className="admin-input-group" style={{ flex: 1.5 }}>
                            <label>Style</label>
                            <select value={block.style || 'btn-secondary'} onChange={e => updateBlock(index, { style: e.target.value })}>
                                <option value="btn-secondary">Secondaire (Blanc)</option>
                                <option value="btn-primary">Principal (Bleu)</option>
                            </select>
                        </div>
                    </div>
                )}

                {block.type === 'image' && (
                    <div style={{ display: 'flex', flexDirection: 'column', gap: '1.25rem' }}>
                        <div className="admin-form-row">
                            <div className="admin-input-group" style={{ flex: 3 }}>
                                <label>Source de l'image (Upload ou URL)</label>
                                <div style={{ display: 'flex', gap: '0.5rem' }}>
                                    <input
                                        placeholder="https://..."
                                        value={block.url || ''}
                                        onChange={e => updateBlock(index, { url: e.target.value })}
                                        style={{ flex: 1 }}
                                    />
                                    <label className="btn btn-sm btn-secondary" style={{ margin: 0, display: 'flex', alignItems: 'center', cursor: 'pointer', whiteSpace: 'nowrap' }}>
                                        {uploading ? '⏳...' : '📁 Choisir'}
                                        <input type="file" accept="image/*" onChange={handleFileChange} style={{ display: 'none' }} disabled={uploading} />
                                    </label>
                                </div>
                                {block.url && (
                                    <div style={{ marginTop: '0.5rem', border: '1px solid var(--admin-border)', borderRadius: '4px', overflow: 'hidden', maxWidth: '200px', maxHeight: '120px' }}>
                                        <img src={block.url} alt="Aperçu" style={{ width: '100%', height: 'auto', display: 'block' }} />
                                    </div>
                                )}
                            </div>
                            <div className="admin-input-group" style={{ flex: 2 }}>
                                <label>Alignement</label>
                                <select value={block.align || 'center'} onChange={e => updateBlock(index, { align: e.target.value })}>
                                    <option value="left">Gauche</option>
                                    <option value="center">Centre</option>
                                    <option value="right">Droite</option>
                                </select>
                            </div>
                        </div>
                        <div className="admin-form-row">
                            <div className="admin-input-group" style={{ flex: 1 }}>
                                <label>Légende de l'image (optionnel)</label>
                                <input placeholder="Décrivez l'image..." value={block.caption || ''} onChange={e => updateBlock(index, { caption: e.target.value })} />
                            </div>
                            <div className="admin-input-group" style={{ flex: 0, minWidth: '120px', justifyContent: 'center', paddingTop: '1.5rem' }}>
                                <label style={{ display: 'flex', alignItems: 'center', gap: '0.5rem', cursor: 'pointer', margin: 0 }}>
                                    <input type="checkbox" checked={block.rounded || false} onChange={e => updateBlock(index, { rounded: e.target.checked })} />
                                    <span>Coins arrondis</span>
                                </label>
                            </div>
                        </div>
                    </div>
                )}

                {block.type === 'video' && (
                    <div className="admin-input-group">
                        <label>Lien de la vidéo (YouTube ou Vimeo)</label>
                        <input placeholder="https://www.youtube.com/watch?v=..." value={block.url || ''} onChange={e => updateBlock(index, { url: e.target.value })} />
                    </div>
                )}

                {block.type === 'callout' && (
                    <div style={{ display: 'flex', flexDirection: 'column', gap: '1.5rem' }}>
                        <div className="admin-form-row">
                            <div className="admin-input-group" style={{ flex: 2 }}>
                                <label>Titre de la note (Optionnel)</label>
                                <input
                                    placeholder="Ex: À noter, Important, Astuce..."
                                    value={block.title || ''}
                                    onChange={e => updateBlock(index, { title: e.target.value })}
                                />
                            </div>
                            <div className="admin-input-group" style={{ flex: 1 }}>
                                <label>Icône / Emoji</label>
                                <input
                                    placeholder="🔔, 💡, ⚠️..."
                                    value={block.icon || ''}
                                    onChange={e => updateBlock(index, { icon: e.target.value })}
                                />
                            </div>
                        </div>
                        <div className="admin-input-group">
                            <label>Style visuel</label>
                            <select value={block.style || 'info'} onChange={e => updateBlock(index, { style: e.target.value })}>
                                <option value="info">💎 Information (Bleu)</option>
                                <option value="warning">🟠 Attention (Orange)</option>
                                <option value="success">🟢 Succès (Vert)</option>
                                <option value="danger">🔴 Danger / Critique (Rouge)</option>
                                <option value="tip">🟣 Astuce / Premium (Violet)</option>
                            </select>
                        </div>
                        <div className="admin-input-group">
                            <label>Contenu du message</label>
                            <TiptapEditor content={block.content || ''} onChange={val => updateBlock(index, { content: val })} />
                        </div>
                    </div>
                )}

                {block.type === 'list' && (
                    <div className="admin-form-row">
                        <div className="admin-input-group" style={{ flex: 0, minWidth: '160px' }}>
                            <label>Style de puce</label>
                            <select value={block.style || 'check'} onChange={e => updateBlock(index, { style: e.target.value })}>
                                <option value="check">✔️ Crochet</option>
                                <option value="rocket">🚀 Fusée</option>
                                <option value="star">⭐ Étoile</option>
                                <option value="number">🔢 Nombres</option>
                            </select>
                        </div>
                        <div className="admin-input-group" style={{ flex: 1 }}>
                            <label>Éléments de la liste (un par ligne)</label>
                            <textarea rows="4" placeholder="Élément 1&#10;Élément 2..." value={block.items ? block.items.join('\n') : ''} onChange={e => updateBlock(index, { items: e.target.value.split('\n') })} />
                        </div>
                    </div>
                )}

                {block.type === 'testimonial' && (
                    <div style={{ display: 'flex', flexDirection: 'column', gap: '1rem' }}>
                        <div className="admin-input-group">
                            <label>Citation / Témoignage</label>
                            <textarea placeholder="Que dit la personne ?" rows="3" value={block.quote || ''} onChange={e => updateBlock(index, { quote: e.target.value })} />
                        </div>
                        <div className="admin-form-row">
                            <div className="admin-input-group">
                                <label>Nom de l'auteur</label>
                                <input placeholder="Ex: Jean Dupont" value={block.author || ''} onChange={e => updateBlock(index, { author: e.target.value })} />
                            </div>
                            <div className="admin-input-group">
                                <label>Rôle ou Entreprise</label>
                                <input placeholder="Ex: CEO chez Google" value={block.role || ''} onChange={e => updateBlock(index, { role: e.target.value })} />
                            </div>
                        </div>
                    </div>
                )}

                {block.type === 'accordion' && (
                    <div>
                        <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: '1rem' }}>
                            <label style={{ margin: 0 }}>Éléments de l'accordéon</label>
                            <button type="button" className="btn btn-xs btn-secondary" onClick={() => updateBlock(index, { items: [...(block.items || []), { title: 'Nouveau titre', content: '' }] })}>
                                + Ajouter un volet
                            </button>
                        </div>
                        <div style={{ display: 'flex', flexDirection: 'column', gap: '0.75rem' }}>
                            {(block.items || []).map((item, iIdx) => (
                                <div key={iIdx} style={{ border: '1px solid var(--admin-border)', padding: '1rem', borderRadius: '8px', background: 'var(--admin-hover-bg)', position: 'relative' }}>
                                    <div style={{ display: 'flex', justifyContent: 'space-between', marginBottom: '0.75rem' }}>
                                        <input placeholder="Titre du volet" value={item.title} onChange={e => { const items = [...block.items]; items[iIdx].title = e.target.value; updateBlock(index, { items }); }} style={{ flex: 1, fontWeight: 'bold' }} />
                                        <button type="button" className="btn-icon delete" onClick={() => { const items = block.items.filter((_, k) => k !== iIdx); updateBlock(index, { items }); }} style={{ marginLeft: '0.75rem' }}>
                                            <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2"><polyline points="3 6 5 6 21 6"></polyline><path d="M19 6v14a2 2 0 0 1-2 2H7a2 2 0 0 1-2-2V6m3 0V4a2 2 0 0 1 2-2h4a2 2 0 0 1 2 2v2"></path></svg>
                                        </button>
                                    </div>
                                    <textarea placeholder="Contenu..." value={item.content} onChange={e => { const items = [...block.items]; items[iIdx].content = e.target.value; updateBlock(index, { items }); }} style={{ width: '100%', minHeight: '80px' }} />
                                </div>
                            ))}
                        </div>
                    </div>
                )}

                {block.type === 'timeline' && (
                    <div>
                        <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: '1rem' }}>
                            <label style={{ margin: 0 }}>Étapes du processus</label>
                            <button type="button" className="btn btn-xs btn-secondary" onClick={() => updateBlock(index, { items: [...(block.items || []), { title: 'Nouvelle étape', content: '', icon: '🎯', tag: '' }] })}>
                                + Ajouter une étape
                            </button>
                        </div>
                        <div style={{ display: 'flex', flexDirection: 'column', gap: '1rem' }}>
                            {(block.items || []).map((step, sIdx) => (
                                <div key={sIdx} style={{ border: '1px solid var(--admin-border)', padding: '1.25rem', borderRadius: '16px', background: 'var(--admin-hover-bg)' }}>
                                    <div className="admin-form-row">
                                        <div className="admin-input-group" style={{ flex: 0, minWidth: '60px' }}>
                                            <label>Icône</label>
                                            <input value={step.icon || ''} onChange={e => { const items = [...block.items]; items[sIdx].icon = e.target.value; updateBlock(index, { items }); }} placeholder="🚀" />
                                        </div>
                                        <div className="admin-input-group" style={{ flex: 2 }}>
                                            <label>Titre de l'étape</label>
                                            <input value={step.title} onChange={e => { const items = [...block.items]; items[sIdx].title = e.target.value; updateBlock(index, { items }); }} />
                                        </div>
                                        <div className="admin-input-group" style={{ flex: 1 }}>
                                            <label>Tag / Délai (Optionnel)</label>
                                            <input value={step.tag || ''} onChange={e => { const items = [...block.items]; items[sIdx].tag = e.target.value; updateBlock(index, { items }); }} placeholder="Ex: J+1, 2 jours..." />
                                        </div>
                                        <button type="button" className="btn-icon delete" onClick={() => { const items = block.items.filter((_, k) => k !== sIdx); updateBlock(index, { items }); }} style={{ marginTop: '1.5rem' }}>🗑️</button>
                                    </div>
                                    <div className="admin-input-group" style={{ marginTop: '1rem' }}>
                                        <label>Description</label>
                                        <TiptapEditor content={step.content || ''} onChange={val => { const items = [...block.items]; items[sIdx].content = val; updateBlock(index, { items }); }} />
                                    </div>
                                </div>
                            ))}
                        </div>
                    </div>
                )}

                {block.type === 'stats' && (
                    <div>
                        <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: '1rem' }}>
                            <label style={{ margin: 0 }}>Chiffres Clés / Statistiques</label>
                            <button type="button" className="btn btn-xs btn-secondary" onClick={() => { updateBlock(index, { items: [...(block.items || []), { value: '10', label: 'Projets', icon: '', color: '#6366f1' }] }); }}>
                                + Ajouter une stat
                            </button>
                        </div>
                        <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(220px, 1fr))', gap: '1rem' }}>
                            {(block.items || []).map((stat, sIdx) => {
                                const handleStatIconUpload = async (e) => {
                                    const file = e.target.files[0];
                                    if (!file) return;
                                    const formData = new FormData();
                                    formData.append('file', file);
                                    try {
                                        const record = await pb.collection('media').create(formData);
                                        const url = pb.files.getUrl(record, record.file);
                                        const items = [...block.items];
                                        items[sIdx].icon = url;
                                        updateBlock(index, { items });
                                    } catch (err) { alert("Erreur upload icône : " + err.message); }
                                };

                                return (
                                    <div key={sIdx} style={{ background: 'var(--admin-hover-bg)', padding: '1rem', borderRadius: '16px', border: '1px solid var(--admin-border)', position: 'relative' }}>
                                        <div style={{ display: 'flex', gap: '1rem', marginBottom: '0.75rem' }}>
                                            <div style={{ width: '50px', height: '50px', background: 'var(--admin-border)', borderRadius: '10px', overflow: 'hidden', position: 'relative', flexShrink: 0 }}>
                                                {stat.icon ? (
                                                    <img src={stat.icon} alt="" style={{ width: '100%', height: '100%', objectFit: 'contain', padding: '5px' }} />
                                                ) : (
                                                    <div style={{ width: '100%', height: '100%', display: 'grid', placeItems: 'center', fontSize: '1.2rem' }}>📊</div>
                                                )}
                                                <input type="file" accept="image/*" onChange={handleStatIconUpload} style={{ position: 'absolute', inset: 0, opacity: 0, cursor: 'pointer', zIndex: 10 }} title="Changer l'icône" />
                                            </div>
                                            <div style={{ flex: 1 }}>
                                                <input placeholder="Nombre" value={stat.value} onChange={e => { const items = [...block.items]; items[sIdx].value = e.target.value; updateBlock(index, { items }); }} style={{ fontWeight: 'bold', fontSize: '1.1rem', marginBottom: '4px' }} />
                                                <input placeholder="Label (ex: Clients)" value={stat.label} onChange={e => { const items = [...block.items]; items[sIdx].label = e.target.value; updateBlock(index, { items }); }} style={{ fontSize: '0.8rem' }} />
                                            </div>
                                        </div>
                                        <div style={{ display: 'flex', alignItems: 'center', gap: '0.5rem' }}>
                                            <label style={{ margin: 0, fontSize: '0.7rem' }}>Couleur</label>
                                            <input type="color" value={stat.color || '#6366f1'} onChange={e => { const items = [...block.items]; items[sIdx].color = e.target.value; updateBlock(index, { items }); }} style={{ width: '30px', height: '24px', padding: 0, border: 'none' }} />
                                        </div>
                                        <button type="button" className="btn-icon delete" onClick={() => { const items = block.items.filter((_, k) => k !== sIdx); updateBlock(index, { items }); }} style={{ position: 'absolute', top: '5px', right: '5px', width: '24px', height: '24px', padding: 0, background: 'rgba(239, 68, 68, 0.9)', color: 'white' }}>×</button>
                                    </div>
                                );
                            })}
                        </div>
                    </div>
                )}

                {block.type === 'perks' && (
                    <div>
                        <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: '1rem' }}>
                            <label style={{ margin: 0 }}>Avantages (Perks)</label>
                            <button type="button" className="btn btn-xs btn-secondary" onClick={() => updateBlock(index, { items: [...(block.items || []), { title: 'Nouvel avantage', icon: '✨' }] })}>
                                + Ajouter un avantage
                            </button>
                        </div>
                        <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(180px, 1fr))', gap: '0.75rem' }}>
                            {(block.items || []).map((perk, pIdx) => (
                                <div key={pIdx} style={{ display: 'flex', gap: '0.5rem', alignItems: 'center', background: 'var(--admin-hover-bg)', padding: '0.5rem', borderRadius: '8px' }}>
                                    <input value={perk.icon} onChange={e => { const items = [...block.items]; items[pIdx].icon = e.target.value; updateBlock(index, { items }); }} style={{ width: '40px', textAlign: 'center' }} />
                                    <input value={perk.title} onChange={e => { const items = [...block.items]; items[pIdx].title = e.target.value; updateBlock(index, { items }); }} style={{ flex: 1 }} />
                                    <button type="button" className="btn-icon delete" onClick={() => { const items = block.items.filter((_, k) => k !== pIdx); updateBlock(index, { items }); }}>🗑️</button>
                                </div>
                            ))}
                        </div>
                    </div>
                )}

                {block.type === 'downloads' && (
                    <div>
                        <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: '1rem' }}>
                            <label style={{ margin: 0 }}>Fichiers à télécharger</label>
                            <button type="button" className="btn btn-xs btn-secondary" onClick={() => updateBlock(index, { items: [...(block.items || []), { label: 'Nouveau fichier', url: '', size: '' }] })}>
                                + Ajouter un fichier
                            </button>
                        </div>
                        <div style={{ display: 'flex', flexDirection: 'column', gap: '1rem' }}>
                            {(block.items || []).map((file, fIdx) => {
                                const handleFileUpload = async (e) => {
                                    const selectedFile = e.target.files[0];
                                    if (!selectedFile) return;

                                    const formData = new FormData();
                                    formData.append('file', selectedFile);

                                    try {
                                        // Upload to 'media' collection
                                        const record = await pb.collection('media').create(formData);
                                        const fileUrl = pb.files.getUrl(record, record.file);
                                        const fileSize = (selectedFile.size / (1024 * 1024)).toFixed(2) + ' MB';

                                        const items = [...block.items];
                                        items[fIdx].url = fileUrl;
                                        items[fIdx].size = fileSize;
                                        if (items[fIdx].label === 'Nouveau fichier') {
                                            items[fIdx].label = selectedFile.name;
                                        }
                                        updateBlock(index, { items });
                                    } catch (err) {
                                        alert("Erreur lors de l'upload : " + err.message);
                                    }
                                };

                                return (
                                    <div key={fIdx} style={{ border: '1px solid var(--admin-border)', padding: '1rem', borderRadius: '12px', background: 'var(--admin-hover-bg)' }}>
                                        <div className="admin-form-row">
                                            <div className="admin-input-group" style={{ flex: 2 }}>
                                                <label>Nom du document</label>
                                                <input value={file.label} onChange={e => { const items = [...block.items]; items[fIdx].label = e.target.value; updateBlock(index, { items }); }} placeholder="Ex: Livret d'accueil" />
                                            </div>
                                            <div className="admin-input-group" style={{ flex: 1 }}>
                                                <label>Upload / Fichier</label>
                                                <div style={{ position: 'relative' }}>
                                                    <input type="file" onChange={handleFileUpload} style={{ position: 'absolute', opacity: 0, inset: 0, cursor: 'pointer', zIndex: 10 }} />
                                                    <button type="button" className="btn btn-xs btn-outline" style={{ width: '100%' }}>
                                                        {file.url ? '🔄 Remplacer' : '📁 Choisir...'}
                                                    </button>
                                                </div>
                                            </div>
                                            <button type="button" className="btn-icon delete" onClick={() => { const items = block.items.filter((_, k) => k !== fIdx); updateBlock(index, { items }); }} style={{ marginTop: '1.5rem' }}>🗑️</button>
                                        </div>
                                        {file.url && (
                                            <div style={{ marginTop: '0.5rem', fontSize: '0.75rem', color: 'var(--admin-primary)', display: 'flex', justifyContent: 'space-between' }}>
                                                <span style={{ overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap', maxWidth: '80%' }}>URL: {file.url}</span>
                                                <span>{file.size}</span>
                                            </div>
                                        )}
                                    </div>
                                );
                            })}
                        </div>
                    </div>
                )}

                {block.type === 'team' && (
                    <div>
                        <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: '1rem' }}>
                            <label style={{ margin: 0 }}>Membres de l'équipe</label>
                            <button type="button" className="btn btn-xs btn-secondary" onClick={() => updateBlock(index, { members: [...(block.members || []), { name: 'Nouveau membre', role: 'Poste', avatar: '' }] })}>
                                + Ajouter un membre
                            </button>
                        </div>
                        <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(250px, 1fr))', gap: '1rem' }}>
                            {(block.members || []).map((member, mIdx) => {
                                const handleAvatarUpload = async (e) => {
                                    const file = e.target.files[0];
                                    if (!file) return;

                                    const formData = new FormData();
                                    formData.append('file', file);

                                    try {
                                        const record = await pb.collection('media').create(formData);
                                        const url = pb.files.getUrl(record, record.file);
                                        const members = [...block.members];
                                        members[mIdx].avatar = url;
                                        updateBlock(index, { members });
                                    } catch (err) {
                                        alert("Erreur upload avatar : " + err.message);
                                    }
                                };

                                const addSocial = () => {
                                    const members = [...block.members];
                                    members[mIdx].socials = [...(members[mIdx].socials || []), { platform: 'linkedin', url: '' }];
                                    updateBlock(index, { members });
                                };

                                const updateSocial = (sIdx, field, value) => {
                                    const members = [...block.members];
                                    const socials = [...(members[mIdx].socials || [])];
                                    socials[sIdx][field] = value;
                                    members[mIdx].socials = socials;
                                    updateBlock(index, { members });
                                };

                                const removeSocial = (sIdx) => {
                                    const members = [...block.members];
                                    members[mIdx].socials = members[mIdx].socials.filter((_, k) => k !== sIdx);
                                    updateBlock(index, { members });
                                };

                                const socialIcons = {
                                    linkedin: <FiLinkedin />,
                                    twitter: <FiTwitter />,
                                    github: <FiGithub />,
                                    instagram: <FiInstagram />,
                                    facebook: <FiFacebook />,
                                    website: <FiGlobe />
                                };

                                return (
                                    <div key={mIdx} style={{ background: 'var(--admin-hover-bg)', padding: '1.25rem', borderRadius: '16px', position: 'relative', border: '1px solid var(--admin-border)', display: 'flex', flexDirection: 'column', gap: '1rem' }}>
                                        <div style={{ display: 'flex', flexDirection: 'column', alignItems: 'center', gap: '0.75rem' }}>
                                            <div style={{ position: 'relative', width: '70px', height: '70px' }}>
                                                <div style={{ width: '100%', height: '100%', borderRadius: '12px', background: 'var(--admin-border)', overflow: 'hidden', border: '2px solid var(--admin-primary)' }}>
                                                    {member.avatar ? (
                                                        <img src={member.avatar} alt="" style={{ width: '100%', height: '100%', objectFit: 'cover' }} />
                                                    ) : (
                                                        <div style={{ width: '100%', height: '100%', display: 'grid', placeItems: 'center', fontSize: '1.5rem' }}>👤</div>
                                                    )}
                                                </div>
                                                <input type="file" accept="image/*" onChange={handleAvatarUpload} style={{ position: 'absolute', inset: 0, opacity: 0, cursor: 'pointer', zIndex: 10 }} title="Changer l'avatar" />
                                            </div>
                                            <input placeholder="Nom Prénom" value={member.name} onChange={e => { const members = [...block.members]; members[mIdx].name = e.target.value; updateBlock(index, { members }); }} style={{ fontWeight: 'bold', textAlign: 'center' }} />
                                            <input placeholder="Poste / Rôle" value={member.role} onChange={e => { const members = [...block.members]; members[mIdx].role = e.target.value; updateBlock(index, { members }); }} style={{ fontSize: '0.85rem', textAlign: 'center' }} />
                                        </div>

                                        <div className="admin-input-group">
                                            <label style={{ fontSize: '0.75rem' }}>Biographie courte</label>
                                            <textarea
                                                placeholder="Quelques mots sur le membre..."
                                                value={member.bio || ''}
                                                onChange={e => { const members = [...block.members]; members[mIdx].bio = e.target.value; updateBlock(index, { members }); }}
                                                style={{ fontSize: '0.85rem', minHeight: '60px' }}
                                            />
                                        </div>

                                        <div className="admin-input-group">
                                            <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: '0.5rem' }}>
                                                <label style={{ margin: 0, fontSize: '0.75rem' }}>Réseaux Sociaux</label>
                                                <button type="button" className="btn btn-xs btn-outline" onClick={addSocial}>+ Ajouter</button>
                                            </div>
                                            <div style={{ display: 'flex', flexDirection: 'column', gap: '0.5rem' }}>
                                                {(member.socials || []).map((soc, sIdx) => (
                                                    <div key={sIdx} style={{ display: 'flex', gap: '0.4rem', alignItems: 'center' }}>
                                                        <select
                                                            value={soc.platform}
                                                            onChange={e => updateSocial(sIdx, 'platform', e.target.value)}
                                                            style={{ padding: '2px', fontSize: '0.8rem', width: 'auto' }}
                                                        >
                                                            <option value="linkedin">LinkedIn</option>
                                                            <option value="twitter">X / Twitter</option>
                                                            <option value="github">GitHub</option>
                                                            <option value="instagram">Instagram</option>
                                                            <option value="facebook">Facebook</option>
                                                            <option value="website">Site Web</option>
                                                        </select>
                                                        <input
                                                            placeholder="URL"
                                                            value={soc.url}
                                                            onChange={e => updateSocial(sIdx, 'url', e.target.value)}
                                                            style={{ fontSize: '0.8rem', flex: 1, padding: '4px' }}
                                                        />
                                                        <button type="button" className="btn-icon delete" onClick={() => removeSocial(sIdx)} style={{ padding: '2px' }}><FiX /></button>
                                                    </div>
                                                ))}
                                            </div>
                                        </div>

                                        <button type="button" className="btn-icon delete" onClick={() => { const members = block.members.filter((_, k) => k !== mIdx); updateBlock(index, { members }); }} style={{ position: 'absolute', top: '5px', right: '5px' }}><FiTrash2 /></button>
                                    </div>
                                );
                            })}
                        </div>
                    </div>
                )}
                {block.type === 'map' && (
                    <div style={{ display: 'flex', flexDirection: 'column', gap: '1rem' }}>
                        <div className="admin-form-row">
                            <div className="admin-input-group" style={{ flex: 2 }}>
                                <label>Libellé / Adresse à afficher</label>
                                <input placeholder="Ex: 5 Rue de Versailles, 78000 Versailles" value={block.address || ''} onChange={e => updateBlock(index, { address: e.target.value })} />
                            </div>
                            <div className="admin-input-group" style={{ flex: 1 }}>
                                <label>Zoom</label>
                                <input type="number" min="1" max="20" value={block.zoom || 13} onChange={e => updateBlock(index, { zoom: parseInt(e.target.value) })} />
                            </div>
                        </div>
                        <div className="admin-input-group" style={{ marginBottom: 0 }}>
                            <label>Lien Google Maps ou Code Embed (Optionnel)</label>
                            <input
                                placeholder="Collez le lien de partage ou le code <iframe>"
                                value={block.mapUrl || ''}
                                onChange={e => updateBlock(index, { mapUrl: e.target.value })}
                            />
                            <p style={{ fontSize: '0.75rem', opacity: 0.6, marginTop: '4px' }}>
                                Si vous collez un lien de partage (maps.app.goo.gl), il sera utilisé pour le bouton d'ouverture. Si vous collez un code d'intégration (&lt;iframe&gt;), il remplacera la carte par défaut.
                            </p>
                        </div>
                    </div>
                )}
                {block.type === 'stack' && (
                    <div>
                        <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: '1rem' }}>
                            <label style={{ margin: 0 }}>Stack Technique / Badges</label>
                            <button type="button" className="btn btn-xs btn-secondary" onClick={() => updateBlock(index, { items: [...(block.items || []), { name: 'Nouvelle Techno', icon: '' }] })}>
                                + Ajouter une techno
                            </button>
                        </div>
                        <div style={{ display: 'flex', flexWrap: 'wrap', gap: '1rem' }}>
                            {(block.items || []).map((item, sIdx) => {
                                // Support old string-only items if they exist
                                const itemObj = typeof item === 'string' ? { name: item, icon: '' } : item;

                                const handleStackIconUpload = async (e) => {
                                    const file = e.target.files[0];
                                    if (!file) return;
                                    const formData = new FormData();
                                    formData.append('file', file);
                                    try {
                                        const record = await pb.collection('media').create(formData);
                                        const url = pb.files.getUrl(record, record.file);
                                        const items = [...block.items];
                                        items[sIdx] = { ...itemObj, icon: url };
                                        updateBlock(index, { items });
                                    } catch (err) { alert("Erreur upload icône stack : " + err.message); }
                                };

                                return (
                                    <div key={sIdx} style={{ display: 'flex', alignItems: 'center', gap: '0.5rem', background: 'var(--admin-hover-bg)', padding: '0.5rem 0.75rem', borderRadius: '100px', border: '1px solid var(--admin-border)' }}>
                                        <div style={{ position: 'relative', width: '24px', height: '24px', flexShrink: 0 }}>
                                            <div style={{ width: '100%', height: '100%', borderRadius: '4px', background: 'rgba(255,255,255,0.1)', overflow: 'hidden', display: 'grid', placeItems: 'center' }}>
                                                {itemObj.icon ? (
                                                    <img src={itemObj.icon} alt="" style={{ width: '100%', height: '100%', objectFit: 'contain' }} />
                                                ) : (
                                                    <span style={{ fontSize: '0.7rem', opacity: 0.5 }}>📁</span>
                                                )}
                                            </div>
                                            <input type="file" accept="image/*" onChange={handleStackIconUpload} style={{ position: 'absolute', inset: 0, opacity: 0, cursor: 'pointer' }} />
                                        </div>
                                        <input
                                            value={itemObj.name}
                                            onChange={e => {
                                                const items = [...block.items];
                                                items[sIdx] = { ...itemObj, name: e.target.value };
                                                updateBlock(index, { items });
                                            }}
                                            style={{ border: 'none', background: 'transparent', padding: 0, width: '100px', fontSize: '0.85rem', fontWeight: 600 }}
                                        />
                                        <button type="button" className="btn-icon delete" onClick={() => { const items = block.items.filter((_, k) => k !== sIdx); updateBlock(index, { items }); }} style={{ width: '20px', height: '20px', padding: 0 }}>×</button>
                                    </div>
                                );
                            })}
                        </div>
                    </div>
                )}

                {block.type === 'spacer' && (
                    <div className="admin-form-row">
                        <div className="admin-input-group">
                            <label>Hauteur (px, rem...)</label>
                            <input placeholder="40px" value={block.height || '40px'} onChange={e => updateBlock(index, { height: e.target.value })} />
                        </div>
                        <div className="admin-input-group" style={{ display: 'flex', alignItems: 'center', paddingTop: '1.5rem' }}>
                            <label style={{ cursor: 'pointer', display: 'flex', alignItems: 'center', gap: '0.5rem' }}>
                                <input type="checkbox" checked={block.showLine || false} onChange={e => updateBlock(index, { showLine: e.target.checked })} />
                                <span>Afficher une ligne de séparation</span>
                            </label>
                        </div>
                    </div>
                )}

                {block.type === 'gallery' && (
                    <div>
                        <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: '1rem' }}>
                            <label style={{ margin: 0 }}>Images de la galerie</label>
                            <button type="button" className="btn btn-xs btn-secondary" onClick={() => updateBlock(index, { images: [...(block.images || []), { url: '', caption: '' }] })}>
                                + Ajouter une image
                            </button>
                        </div>
                        <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(180px, 1fr))', gap: '1rem' }}>
                            {(block.images || []).map((img, iIdx) => {
                                const handleGalleryUpload = async (e) => {
                                    const file = e.target.files[0];
                                    if (!file) return;
                                    const formData = new FormData();
                                    formData.append('file', file);
                                    try {
                                        const record = await pb.collection('media').create(formData);
                                        const url = pb.files.getUrl(record, record.file);
                                        const images = [...block.images];
                                        images[iIdx].url = url;
                                        updateBlock(index, { images });
                                    } catch (err) { alert("Erreur upload galerie : " + err.message); }
                                };

                                return (
                                    <div key={iIdx} style={{ background: 'var(--admin-hover-bg)', padding: '0.75rem', borderRadius: '16px', border: '1px solid var(--admin-border)', position: 'relative' }}>
                                        <div style={{ width: '100%', height: '120px', background: 'var(--admin-border)', borderRadius: '10px', marginBottom: '0.75rem', overflow: 'hidden', position: 'relative' }}>
                                            {img.url ? (
                                                <img src={img.url} alt="" style={{ width: '100%', height: '100%', objectFit: 'cover' }} />
                                            ) : (
                                                <div style={{ width: '100%', height: '100%', display: 'grid', placeItems: 'center', fontSize: '2rem' }}>🖼️</div>
                                            )}
                                            <input type="file" accept="image/*" onChange={handleGalleryUpload} style={{ position: 'absolute', inset: 0, opacity: 0, cursor: 'pointer', zIndex: 10 }} title="Uploader une image" />
                                        </div>
                                        <input
                                            placeholder="Légende..."
                                            value={img.caption}
                                            onChange={e => { const images = [...block.images]; images[iIdx].caption = e.target.value; updateBlock(index, { images }); }}
                                            style={{ fontSize: '0.8rem', width: '100%', border: 'none', background: 'var(--admin-card-bg)', padding: '4px 8px', borderRadius: '4px' }}
                                        />
                                        <button type="button" className="btn-icon delete" onClick={() => { const images = block.images.filter((_, k) => k !== iIdx); updateBlock(index, { images }); }} style={{ position: 'absolute', top: '5px', right: '5px', width: '24px', height: '24px', padding: 0, background: 'rgba(239, 68, 68, 0.9)', color: 'white' }}>×</button>
                                    </div>
                                );
                            })}
                        </div>
                    </div>
                )}

                {block.type === 'highlight' && (
                    <div style={{ display: 'flex', gap: '1rem' }}>
                        <div className="admin-input-group" style={{ flex: 1 }}>
                            <label>Texte de la bannière</label>
                            <input value={block.text || ''} onChange={e => updateBlock(index, { text: e.target.value })} placeholder="Ex: Recrutement ouvert jusqu'au 31 Mars !" />
                        </div>
                        <div className="admin-input-group" style={{ width: '150px' }}>
                            <label>Style</label>
                            <select value={block.style || 'gradient'} onChange={e => updateBlock(index, { style: e.target.value })}>
                                <option value="gradient">🌈 Dégradé</option>
                                <option value="danger">🚨 Urgence (Rouge)</option>
                                <option value="success">✅ Succès (Vert)</option>
                            </select>
                        </div>
                    </div>
                )}

                {block.type === 'sidebyside' && (
                    <div style={{ display: 'flex', flexDirection: 'column', gap: '1.5rem' }}>
                        <div style={{ display: 'grid', gridTemplateColumns: 'minmax(300px, 1fr) 1.5fr', gap: '2rem' }}>
                            {/* Zone Média (Upload & Preview) */}
                            <div className="admin-input-group">
                                <label style={{ color: 'var(--admin-primary)', fontWeight: 'bold', display: 'flex', alignItems: 'center', gap: '8px', marginBottom: '1rem' }}>
                                    <span>💎 Média (Image ou Vidéo)</span>
                                </label>

                                <div style={{
                                    border: '2px dashed var(--admin-border)',
                                    borderRadius: '20px',
                                    padding: '1.5rem',
                                    background: 'var(--admin-hover-bg)',
                                    display: 'flex',
                                    flexDirection: 'column',
                                    gap: '1rem',
                                    transition: 'all 0.3s ease',
                                    position: 'relative',
                                    minHeight: '220px',
                                    justifyContent: 'center',
                                    alignItems: 'center',
                                    textAlign: 'center',
                                    boxShadow: 'inset 0 2px 10px rgba(0,0,0,0.02)'
                                }}>
                                    {block.mediaUrl ? (
                                        <div style={{ width: '100%', position: 'relative', animation: 'adminScaleIn 0.3s ease' }}>
                                            {block.mediaType === 'video' ? (
                                                <div style={{ background: '#0f172a', borderRadius: '14px', overflow: 'hidden', aspectRatio: '16/9', display: 'grid', placeItems: 'center', border: '1px solid var(--admin-border)' }}>
                                                    <span style={{ fontSize: '3rem' }}>🎥</span>
                                                    <div style={{ position: 'absolute', bottom: '10px', left: '10px', background: 'rgba(0,0,0,0.5)', padding: '4px 10px', borderRadius: '6px', fontSize: '0.7rem', color: 'white' }}>Vidéo sélectionnée</div>
                                                </div>
                                            ) : (
                                                <div style={{ position: 'relative' }}>
                                                    <img src={block.mediaUrl} alt="Preview" style={{ width: '100%', maxHeight: '250px', objectFit: 'contain', borderRadius: '14px', boxShadow: '0 8px 25px rgba(0,0,0,0.15)', border: '1px solid var(--admin-border)' }} />
                                                    <div style={{ position: 'absolute', bottom: '10px', left: '10px', background: 'rgba(255,255,255,0.8)', padding: '4px 10px', borderRadius: '6px', fontSize: '0.7rem', color: '#000', backdropFilter: 'blur(4px)' }}>Image sélectionnée</div>
                                                </div>
                                            )}
                                            <button
                                                type="button"
                                                onClick={(e) => { e.stopPropagation(); updateBlock(index, { mediaUrl: '' }); }}
                                                style={{ position: 'absolute', top: '-12px', right: '-12px', width: '32px', height: '32px', borderRadius: '50%', background: '#ef4444', color: 'white', border: '2px solid white', cursor: 'pointer', display: 'grid', placeItems: 'center', fontSize: '1.4rem', boxShadow: '0 4px 12px rgba(239, 68, 68, 0.3)', zIndex: 12 }}
                                                title="Supprimer le média"
                                            >
                                                ×
                                            </button>
                                        </div>
                                    ) : (
                                        <div style={{ padding: '2rem 0' }}>
                                            <div style={{ fontSize: '3.5rem', marginBottom: '1rem', filter: 'drop-shadow(0 4px 10px rgba(0,0,0,0.1))' }}>📁</div>
                                            <p style={{ margin: 0, fontWeight: '700', fontSize: '1rem', color: 'var(--admin-text)' }}>Cliquez pour uploader un média</p>
                                            <p style={{ margin: '0.5rem 0 0', fontSize: '0.85rem', color: 'var(--admin-text-muted)' }}>Image (JPG, PNG, WebP) ou Vidéo</p>
                                        </div>
                                    )}

                                    <input
                                        type="file"
                                        accept="image/*,video/*"
                                        style={{ position: 'absolute', inset: 0, opacity: 0, cursor: 'pointer', zIndex: 10 }}
                                        onChange={async (e) => {
                                            const file = e.target.files[0];
                                            if (!file) return;
                                            const formData = new FormData();
                                            formData.append('file', file);
                                            try {
                                                const record = await pb.collection('media').create(formData);
                                                const url = pb.files.getUrl(record, record.file);
                                                updateBlock(index, {
                                                    mediaUrl: url,
                                                    mediaType: file.type.startsWith('video') ? 'video' : 'image'
                                                });
                                            } catch (err) { alert("Erreur upload : " + err.message); }
                                        }}
                                    />
                                </div>

                            </div>

                            {/* Configuration & Texte */}
                            <div style={{ display: 'flex', flexDirection: 'column', gap: '1.5rem' }}>
                                <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: '1rem' }}>
                                    <div className="admin-input-group">
                                        <label>Type de contenu</label>
                                        <select value={block.mediaType || 'image'} onChange={e => updateBlock(index, { mediaType: e.target.value })}>
                                            <option value="image">🖼️ Afficher comme Image</option>
                                            <option value="video">🎥 Afficher comme Vidéo</option>
                                        </select>
                                    </div>
                                    <div className="admin-input-group">
                                        <label>Position du média</label>
                                        <select value={block.imageRight ? 'right' : 'left'} onChange={e => updateBlock(index, { imageRight: e.target.value === 'right' })}>
                                            <option value="left">⬅️ Média à gauche</option>
                                            <option value="right">➡️ Média à droite</option>
                                        </select>
                                    </div>
                                </div>

                                <div className="admin-input-group" style={{ flex: 1 }}>
                                    <label>Contenu textuel (HTML supporté)</label>
                                    <div style={{ background: 'white', borderRadius: '12px', overflow: 'hidden', border: '1px solid var(--admin-border)' }}>
                                        <TiptapEditor content={block.text || ''} onChange={val => updateBlock(index, { text: val })} />
                                    </div>
                                </div>
                            </div>
                        </div>
                    </div>
                )}

                {block.type === 'tabs' && (
                    <div>
                        <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: '1rem' }}>
                            <label style={{ margin: 0 }}>Onglets</label>
                            <button type="button" className="btn btn-xs btn-secondary" onClick={() => updateBlock(index, { items: [...(block.items || []), { title: 'Nouvel onglet', content: '' }] })}>
                                + Ajouter
                            </button>
                        </div>
                        <div style={{ display: 'flex', flexDirection: 'column', gap: '1rem' }}>
                            {(block.items || []).map((tab, tIdx) => (
                                <div key={tIdx} style={{ border: '1px solid var(--admin-border)', padding: '1rem', borderRadius: '12px', background: 'var(--admin-hover-bg)' }}>
                                    <div style={{ display: 'flex', gap: '0.5rem', marginBottom: '0.75rem' }}>
                                        <input placeholder="Titre de l'onglet" value={tab.title} onChange={e => { const items = [...block.items]; items[tIdx].title = e.target.value; updateBlock(index, { items }); }} style={{ flex: 1, fontWeight: 'bold' }} />
                                        <button type="button" className="btn-icon delete" onClick={() => { const items = block.items.filter((_, k) => k !== tIdx); updateBlock(index, { items }); }}>🗑️</button>
                                    </div>
                                    <TiptapEditor content={tab.content || ''} onChange={val => { const items = [...block.items]; items[tIdx].content = val; updateBlock(index, { items }); }} />
                                </div>
                            ))}
                        </div>
                    </div>
                )}

                {block.type === 'beforeafter' && (
                    <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: '1.5rem' }}>
                        {/* Image Avant */}
                        <div className="admin-input-group">
                            <label style={{ display: 'flex', alignItems: 'center', gap: '8px', fontWeight: 'bold', color: 'var(--admin-primary)' }}>
                                <span>⬅️ Image "Avant"</span>
                            </label>
                            <div style={{
                                border: '2px dashed var(--admin-border)',
                                borderRadius: '16px',
                                padding: '1rem',
                                background: 'var(--admin-hover-bg)',
                                textAlign: 'center',
                                position: 'relative',
                                minHeight: '150px',
                                display: 'grid',
                                placeItems: 'center',
                                transition: 'all 0.3s ease'
                            }}>
                                {block.beforeUrl ? (
                                    <div style={{ position: 'relative', width: '100%', animation: 'adminScaleIn 0.3s ease' }}>
                                        <img src={block.beforeUrl} alt="Avant" style={{ maxWidth: '100%', maxHeight: '120px', borderRadius: '10px', objectFit: 'contain', boxShadow: '0 4px 12px rgba(0,0,0,0.1)' }} />
                                        <button
                                            type="button"
                                            onClick={(e) => { e.stopPropagation(); updateBlock(index, { beforeUrl: '' }); }}
                                            style={{ position: 'absolute', top: '-10px', right: '-10px', width: '28px', height: '28px', borderRadius: '50%', background: '#ef4444', color: 'white', border: '2px solid white', cursor: 'pointer', display: 'grid', placeItems: 'center', fontSize: '1.2rem', boxShadow: '0 4px 8px rgba(0,0,0,0.2)', zIndex: 12 }}
                                        >×</button>
                                    </div>
                                ) : (
                                    <div>
                                        <div style={{ fontSize: '2.5rem', marginBottom: '0.5rem' }}>🖼️</div>
                                        <div style={{ fontSize: '0.85rem', fontWeight: 'bold', color: 'var(--admin-text)' }}>Uploader "Avant"</div>
                                        <div style={{ fontSize: '0.7rem', color: 'var(--admin-text-muted)' }}>Cliquez ou glissez</div>
                                    </div>
                                )}
                                <input
                                    type="file"
                                    accept="image/*"
                                    style={{ position: 'absolute', inset: 0, opacity: 0, cursor: 'pointer', zIndex: 10 }}
                                    onChange={async (e) => {
                                        const file = e.target.files[0];
                                        if (!file) return;
                                        const formData = new FormData();
                                        formData.append('file', file);
                                        try {
                                            const record = await pb.collection('media').create(formData);
                                            const url = pb.files.getUrl(record, record.file);
                                            updateBlock(index, { beforeUrl: url });
                                        } catch (err) { alert("Erreur upload : " + err.message); }
                                    }}
                                />
                            </div>
                        </div>

                        {/* Image Après */}
                        <div className="admin-input-group">
                            <label style={{ display: 'flex', alignItems: 'center', gap: '8px', fontWeight: 'bold', color: 'var(--admin-primary)' }}>
                                <span>➡️ Image "Après"</span>
                            </label>
                            <div style={{
                                border: '2px dashed var(--admin-border)',
                                borderRadius: '16px',
                                padding: '1rem',
                                background: 'var(--admin-hover-bg)',
                                textAlign: 'center',
                                position: 'relative',
                                minHeight: '150px',
                                display: 'grid',
                                placeItems: 'center',
                                transition: 'all 0.3s ease'
                            }}>
                                {block.afterUrl ? (
                                    <div style={{ position: 'relative', width: '100%', animation: 'adminScaleIn 0.3s ease' }}>
                                        <img src={block.afterUrl} alt="Après" style={{ maxWidth: '100%', maxHeight: '120px', borderRadius: '10px', objectFit: 'contain', boxShadow: '0 4px 12px rgba(0,0,0,0.1)' }} />
                                        <button
                                            type="button"
                                            onClick={(e) => { e.stopPropagation(); updateBlock(index, { afterUrl: '' }); }}
                                            style={{ position: 'absolute', top: '-10px', right: '-10px', width: '28px', height: '28px', borderRadius: '50%', background: '#ef4444', color: 'white', border: '2px solid white', cursor: 'pointer', display: 'grid', placeItems: 'center', fontSize: '1.2rem', boxShadow: '0 4px 8px rgba(0,0,0,0.2)', zIndex: 12 }}
                                        >×</button>
                                    </div>
                                ) : (
                                    <div>
                                        <div style={{ fontSize: '2.5rem', marginBottom: '0.5rem' }}>✨</div>
                                        <div style={{ fontSize: '0.85rem', fontWeight: 'bold', color: 'var(--admin-text)' }}>Uploader "Après"</div>
                                        <div style={{ fontSize: '0.7rem', color: 'var(--admin-text-muted)' }}>Cliquez ou glissez</div>
                                    </div>
                                )}
                                <input
                                    type="file"
                                    accept="image/*"
                                    style={{ position: 'absolute', inset: 0, opacity: 0, cursor: 'pointer', zIndex: 10 }}
                                    onChange={async (e) => {
                                        const file = e.target.files[0];
                                        if (!file) return;
                                        const formData = new FormData();
                                        formData.append('file', file);
                                        try {
                                            const record = await pb.collection('media').create(formData);
                                            const url = pb.files.getUrl(record, record.file);
                                            updateBlock(index, { afterUrl: url });
                                        } catch (err) { alert("Erreur upload : " + err.message); }
                                    }}
                                />
                            </div>
                        </div>
                    </div>
                )}

                {block.type === 'countdown' && (
                    <div className="admin-form-row">
                        <div className="admin-input-group" style={{ flex: 2 }}>
                            <label>Libellé de l'urgence</label>
                            <input value={block.label || ''} onChange={e => updateBlock(index, { label: e.target.value })} placeholder="Ex: Fin de l'offre dans :" />
                        </div>
                        <div className="admin-input-group" style={{ flex: 1 }}>
                            <label>Date de fin (ISO ou String)</label>
                            <input type="datetime-local" value={block.endDate || ''} onChange={e => updateBlock(index, { endDate: e.target.value })} />
                        </div>
                    </div>
                )}

                {block.type === 'marquee' && (
                    <div>
                        <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: '1.5rem' }}>
                            <label style={{ margin: 0, fontWeight: 'bold', color: 'var(--admin-primary)', display: 'flex', alignItems: 'center', gap: '8px' }}>
                                <span>🛡️ Défilement de Logos (Marquee)</span>
                            </label>
                            <div style={{ fontSize: '0.8rem', color: 'var(--admin-text-muted)' }}>
                                {block.images?.length || 0} logo(s) ajouté(s)
                            </div>
                        </div>

                        <div style={{
                            display: 'grid',
                            gridTemplateColumns: 'repeat(auto-fill, minmax(130px, 1fr))',
                            gap: '1rem',
                            background: 'var(--admin-hover-bg)',
                            padding: '1.5rem',
                            borderRadius: '20px',
                            border: '1px solid var(--admin-border)'
                        }}>
                            {(block.images || []).map((img, iIdx) => (
                                <div key={iIdx} style={{
                                    position: 'relative',
                                    aspectRatio: '3/2',
                                    background: 'white',
                                    borderRadius: '12px',
                                    border: '1px solid var(--admin-border)',
                                    display: 'grid',
                                    placeItems: 'center',
                                    padding: '10px',
                                    animation: 'adminScaleIn 0.3s ease',
                                    boxShadow: '0 4px 10px rgba(0,0,0,0.03)'
                                }}>
                                    <img src={img.url} alt="Logo" style={{ maxWidth: '100%', maxHeight: '100%', objectFit: 'contain' }} />
                                    <button
                                        type="button"
                                        className="btn-icon delete"
                                        onClick={() => { const images = block.images.filter((_, k) => k !== iIdx); updateBlock(index, { images }); }}
                                        style={{ position: 'absolute', top: '-8px', right: '-8px', width: '24px', height: '24px', background: '#ef4444', color: 'white', borderRadius: '50%', border: '2px solid white', fontSize: '1rem' }}
                                    >×</button>
                                </div>
                            ))}

                            {/* Bouton d'ajout d'un nouveau logo */}
                            <div style={{
                                position: 'relative',
                                aspectRatio: '3/2',
                                border: '2px dashed var(--admin-border)',
                                borderRadius: '12px',
                                display: 'flex',
                                flexDirection: 'column',
                                alignItems: 'center',
                                justifyContent: 'center',
                                gap: '4px',
                                cursor: 'pointer',
                                transition: 'all 0.2s ease',
                                background: 'transparent'
                            }} onMouseOver={e => e.currentTarget.style.borderColor = 'var(--admin-primary)'} onMouseOut={e => e.currentTarget.style.borderColor = 'var(--admin-border)'}>
                                <span style={{ fontSize: '1.5rem' }}>➕</span>
                                <span style={{ fontSize: '0.7rem', fontWeight: 'bold', color: 'var(--admin-text-muted)' }}>Ajouter un logo</span>
                                <input
                                    type="file"
                                    accept="image/*"
                                    style={{ position: 'absolute', inset: 0, opacity: 0, cursor: 'pointer' }}
                                    onChange={async (e) => {
                                        const file = e.target.files[0];
                                        if (!file) return;
                                        const formData = new FormData();
                                        formData.append('file', file);
                                        try {
                                            const record = await pb.collection('media').create(formData);
                                            const url = pb.files.getUrl(record, record.file);
                                            const images = [...(block.images || []), { url }];
                                            updateBlock(index, { images });
                                        } catch (err) { alert("Erreur upload logo : " + err.message); }
                                    }}
                                />
                            </div>
                        </div>

                        <div style={{ marginTop: '1rem', display: 'flex', gap: '1.5rem', alignItems: 'center' }}>
                            <div className="admin-input-group" style={{ flex: 1 }}>
                                <label style={{ fontSize: '0.8rem' }}>Vitesse de défilement</label>
                                <div style={{ display: 'flex', alignItems: 'center', gap: '1rem' }}>
                                    <input type="range" min="10" max="100" step="5" value={block.speed || 20} onChange={e => updateBlock(index, { speed: parseInt(e.target.value) })} style={{ flex: 1 }} />
                                    <span style={{ fontSize: '0.85rem', fontWeight: 'bold', minWidth: '40px' }}>{block.speed || 20}s</span>
                                </div>
                            </div>
                        </div>
                    </div>
                )}

                {block.type === 'tooltip' && (
                    <div className="admin-form-row">
                        <div className="admin-input-group" style={{ flex: 1 }}>
                            <label>Texte visible</label>
                            <input value={block.text || ''} onChange={e => updateBlock(index, { text: e.target.value })} />
                        </div>
                        <div className="admin-input-group" style={{ flex: 1 }}>
                            <label>Contenu de l'info-bulle (Survol)</label>
                            <input value={block.tooltip || ''} onChange={e => updateBlock(index, { tooltip: e.target.value })} />
                        </div>
                    </div>
                )}

                {block.type === 'codesnippet' && (
                    <div style={{ display: 'flex', flexDirection: 'column', gap: '1rem' }}>
                        <div className="admin-input-group" style={{ width: '150px' }}>
                            <label>Langage</label>
                            <select value={block.language || 'javascript'} onChange={e => updateBlock(index, { language: e.target.value })}>
                                <option value="javascript">JavaScript</option>
                                <option value="python">Python</option>
                                <option value="html">HTML</option>
                                <option value="css">CSS</option>
                            </select>
                        </div>
                        <div className="admin-input-group">
                            <label>Code</label>
                            <textarea rows="6" value={block.code || ''} onChange={e => updateBlock(index, { code: e.target.value })} style={{ fontFamily: 'monospace', fontSize: '0.9rem' }} />
                        </div>
                    </div>
                )}

                {block.type === 'awards' && (
                    <div>
                        <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: '1.5rem' }}>
                            <label style={{ margin: 0, fontWeight: 'bold', color: 'var(--admin-primary)', display: 'flex', alignItems: 'center', gap: '8px' }}>
                                <span>🏆 Récompenses & Certifications</span>
                            </label>
                            <button type="button" className="btn btn-xs btn-primary" onClick={() => updateBlock(index, { items: [...(block.items || []), { title: 'Nouvelle certification', image: '' }] })}>
                                + Ajouter une récompense
                            </button>
                        </div>

                        <div style={{
                            display: 'grid',
                            gridTemplateColumns: 'repeat(auto-fill, minmax(200px, 1fr))',
                            gap: '1.25rem'
                        }}>
                            {(block.items || []).map((item, iIdx) => {
                                const handleAwardUpload = async (e) => {
                                    const file = e.target.files[0];
                                    if (!file) return;
                                    const formData = new FormData();
                                    formData.append('file', file);
                                    try {
                                        const record = await pb.collection('media').create(formData);
                                        const url = pb.files.getUrl(record, record.file);
                                        const items = [...block.items];
                                        items[iIdx].image = url;
                                        updateBlock(index, { items });
                                    } catch (err) { alert("Erreur upload badge : " + err.message); }
                                };

                                return (
                                    <div key={iIdx} style={{
                                        background: 'var(--admin-hover-bg)',
                                        padding: '1.25rem',
                                        borderRadius: '20px',
                                        border: '1px solid var(--admin-border)',
                                        display: 'flex',
                                        flexDirection: 'column',
                                        gap: '1rem',
                                        position: 'relative',
                                        animation: 'adminScaleIn 0.3s ease',
                                        textAlign: 'center'
                                    }}>
                                        <div style={{
                                            aspectRatio: '1/1',
                                            background: 'white',
                                            borderRadius: '16px',
                                            border: '1px solid var(--admin-border)',
                                            position: 'relative',
                                            display: 'grid',
                                            placeItems: 'center',
                                            overflow: 'hidden',
                                            boxShadow: '0 4px 12px rgba(0,0,0,0.05)'
                                        }}>
                                            {item.image ? (
                                                <img src={item.image} alt="" style={{ maxWidth: '80%', maxHeight: '80%', objectFit: 'contain' }} />
                                            ) : (
                                                <div style={{ fontSize: '2.5rem' }}>🏅</div>
                                            )}
                                            <input
                                                type="file"
                                                accept="image/*"
                                                style={{ position: 'absolute', inset: 0, opacity: 0, cursor: 'pointer', zIndex: 10 }}
                                                onChange={handleAwardUpload}
                                                title="Uploader le badge"
                                            />
                                        </div>

                                        <div className="admin-input-group" style={{ marginBottom: 0 }}>
                                            <input
                                                placeholder="Titre (ex: ISO 9001)"
                                                value={item.title}
                                                onChange={e => { const items = [...block.items]; items[iIdx].title = e.target.value; updateBlock(index, { items }); }}
                                                style={{ fontSize: '0.9rem', fontWeight: 'bold', textAlign: 'center' }}
                                            />
                                        </div>

                                        <button
                                            type="button"
                                            className="btn-icon delete"
                                            onClick={() => { const items = block.items.filter((_, k) => k !== iIdx); updateBlock(index, { items }); }}
                                            style={{ position: 'absolute', top: '5px', right: '5px', background: 'rgba(239, 68, 68, 0.1)', color: '#ef4444', width: '28px', height: '28px' }}
                                        >×</button>
                                    </div>
                                );
                            })}
                        </div>
                    </div>
                )}

                {block.type === 'widget' && (
                    <div style={{ display: 'flex', flexDirection: 'column', gap: '1.5rem', position: 'relative' }}>
                        {showHelp && (
                            <div style={{
                                position: 'fixed',
                                top: 0,
                                left: 0,
                                right: 0,
                                bottom: 0,
                                zIndex: 9999,
                                background: 'rgba(15, 23, 42, 0.9)',
                                backdropFilter: 'blur(8px)',
                                display: 'flex',
                                alignItems: 'center',
                                justifyContent: 'center',
                                padding: '2rem'
                            }}>
                                {widgetHelp}
                            </div>
                        )}

                        {showCreateModal && (
                            <div style={{
                                position: 'fixed',
                                top: 0, left: 0, right: 0, bottom: 0,
                                zIndex: 10000,
                                background: 'rgba(0,0,0,0.8)',
                                display: 'flex', alignItems: 'center', justifyContent: 'center'
                            }}>
                                <div style={{ background: 'var(--admin-card-bg)', padding: '2rem', borderRadius: '24px', width: '800px', maxWidth: '95vw', boxShadow: '0 25px 60px rgba(0,0,0,0.5)', border: '1px solid var(--admin-border)' }}>
                                    <h3 style={{ marginTop: 0, color: 'var(--admin-primary)' }}>🛠️ Nouveau Composant Bibliothèque</h3>

                                    <div className="admin-form-row" style={{ marginTop: '1.5rem' }}>
                                        <div className="admin-input-group" style={{ flex: 1 }}>
                                            <label>Titre du widget</label>
                                            <input
                                                autoFocus
                                                placeholder="Ex: Calculateur Salaire v1"
                                                value={newWidgetTitle}
                                                onChange={e => setNewWidgetTitle(e.target.value)}
                                            />
                                        </div>
                                    </div>

                                    <div className="admin-input-group" style={isFullscreen ? {
                                        position: 'fixed',
                                        inset: 0,
                                        zIndex: 11000,
                                        background: '#1e1e1e',
                                        padding: '2rem',
                                        display: 'flex',
                                        flexDirection: 'column',
                                        margin: 0
                                    } : { marginTop: '1.5rem' }}>
                                        <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: '0.5rem' }}>
                                            <label style={isFullscreen ? { color: 'white' } : {}}>Code React / JSX</label>
                                            <button
                                                type="button"
                                                className="btn btn-xs"
                                                onClick={() => setIsFullscreen(!isFullscreen)}
                                                style={isFullscreen ? { color: 'white', borderColor: 'rgba(255,255,255,0.4)', background: 'rgba(255,255,255,0.15)' } : {}}
                                            >
                                                {isFullscreen ? '↙ Quitter le plein écran' : '📂 Plein écran'}
                                            </button>
                                        </div>
                                        <textarea
                                            rows={isFullscreen ? "30" : "15"}
                                            style={{
                                                fontFamily: 'monospace',
                                                fontSize: '0.85rem',
                                                background: '#1e1e1e',
                                                color: '#d4d4d4',
                                                padding: '1rem',
                                                borderRadius: '12px',
                                                flex: isFullscreen ? 1 : 'unset'
                                            }}
                                            value={newWidgetCode}
                                            onChange={e => setNewWidgetCode(e.target.value)}
                                            placeholder="const MyWidget = () => { return <div>Hello</div> }; render(<MyWidget />);"
                                        />
                                    </div>

                                    <div className="admin-input-group" style={{ width: '150px' }}>
                                        <label>Type de code</label>
                                        <select value={editingWidgetId && availableWidgets.find(x => x.id === editingWidgetId)?.type || 'react'} onChange={e => {
                                            // On ne peut pas changer le type d'un widget existant facilement ici sans refacto, 
                                            // mais on peut au moins l'afficher ou l'initialiser
                                        }}>
                                            <option value="react">React / JSX</option>
                                            <option value="html">HTML / CSS / JS</option>
                                        </select>
                                    </div>
                                    <div style={{ display: 'flex', gap: '1rem', marginTop: '2rem' }}>
                                        <button className="btn btn-outline" style={{ flex: 1 }} onClick={() => { setShowCreateModal(false); setEditingWidgetId(null); }}>Annuler</button>
                                        <button className="btn btn-primary" style={{ flex: 1, fontWeight: 'bold' }} onClick={handleSaveToLibrary} disabled={isSaving}>
                                            {isSaving ? 'Enregistrement...' : editingWidgetId ? '💾 Mettre à jour' : '🚀 Enregistrer dans la bibliothèque'}
                                        </button>
                                    </div>
                                </div>
                            </div>
                        )}

                        <div className="admin-form-row">
                            <div className="admin-input-group" style={{ flex: 1 }}>
                                <div style={{ display: 'flex', alignItems: 'center', gap: '0.5rem', marginBottom: '0.5rem' }}>
                                    <label style={{ margin: 0 }}>Mode de fonctionnement</label>
                                    <button type="button" onClick={() => setShowHelp(true)} style={{ background: 'var(--admin-primary)', color: 'white', border: 'none', borderRadius: '50%', width: '18px', height: '18px', fontSize: '12px', fontWeight: 'bold', cursor: 'pointer', display: 'grid', placeItems: 'center' }}>?</button>
                                </div>
                                <select value={block.mode || 'library'} onChange={e => updateBlock(index, { mode: e.target.value })}>
                                    <option value="library">📚 Bibliothèque (Composant réutilisable)</option>
                                    <option value="custom">✍️ Code JSX personnalisé (Unique)</option>
                                </select>
                            </div>
                            <div className="admin-input-group" style={{ width: '180px' }}>
                                <label>Apparence / Thème</label>
                                <select value={block.themeOverride || 'auto'} onChange={e => updateBlock(index, { themeOverride: e.target.value })}>
                                    <option value="auto">🌓 Auto (Site)</option>
                                    <option value="light">☀️ Clair forcé</option>
                                    <option value="dark">🌙 Sombre forcé</option>
                                </select>
                            </div>
                        </div>

                        {block.mode === 'library' ? (
                            <div className="admin-input-group">
                                <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: '0.5rem' }}>
                                    <label style={{ margin: 0 }}>Choisir un widget dans votre bibliothèque</label>
                                    <div style={{ display: 'flex', gap: '4px' }}>
                                        <button type="button" className="btn btn-xs btn-outline" onClick={onOpenLibrary}>📚 Bibliothèque</button>
                                        <button type="button" className="btn btn-xs btn-outline" onClick={() => { setNewWidgetTitle(''); setNewWidgetCode(''); setEditingWidgetId(null); setShowCreateModal(true); }}>➕ Nouveau</button>
                                    </div>
                                </div>
                                <div style={{ display: 'flex', gap: '0.5rem' }}>
                                    <select value={block.widgetId || ''} onChange={e => updateBlock(index, { widgetId: e.target.value })} style={{ flex: 1 }}>
                                        <option value="">-- Sélectionner un widget --</option>
                                        {(availableWidgets || []).map(w => (
                                            <option key={w.id} value={w.id}>
                                                {w.title} ({w.type})
                                            </option>
                                        ))}
                                    </select>
                                    {block.widgetId && (
                                        <div style={{ display: 'flex', gap: '4px' }}>
                                            <button type="button" className="btn btn-xs btn-outline" onClick={handleEditLibraryWidget} title="Modifier le modèle">✏️</button>
                                            <button type="button" className="btn btn-xs btn-outline" onClick={handleDetachWidget} title="Détacher (Copie unique)">✂️</button>
                                            <button type="button" className="btn btn-xs btn-danger" onClick={handleDeleteLibraryWidget} title="Supprimer de la bibliothèque">🗑️</button>
                                        </div>
                                    )}
                                </div>
                                {(!availableWidgets || availableWidgets.length === 0) && (
                                    <p className="text-muted" style={{ fontSize: '0.8rem', marginTop: '0.5rem' }}>
                                        Aucun widget trouvé dans la collection <code>custom_widgets</code>.
                                    </p>
                                )}
                            </div>
                        ) : (
                            <div className="admin-input-group" style={isCustomFullscreen ? {
                                position: 'fixed',
                                inset: 0,
                                zIndex: 11000,
                                background: '#1e1e1e',
                                padding: '2rem',
                                display: 'flex',
                                flexDirection: 'column',
                                margin: 0
                            } : {}}>
                                <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: '0.5rem' }}>
                                    <label style={isCustomFullscreen ? { color: 'white' } : { margin: 0 }}>Code JSX / React (Mode Live)</label>
                                    <div style={{ display: 'flex', gap: '8px' }}>
                                        <button
                                            type="button"
                                            className="btn btn-xs"
                                            onClick={() => setIsCustomFullscreen(!isCustomFullscreen)}
                                            style={isCustomFullscreen ? { color: 'white', borderColor: 'rgba(255,255,255,0.4)', background: 'rgba(255,255,255,0.15)' } : {}}
                                        >
                                            {isCustomFullscreen ? '↙ Quitter le plein écran' : '📂 Plein écran'}
                                        </button>
                                        {!isCustomFullscreen && (
                                            <button type="button" className="btn btn-xs btn-secondary" onClick={() => { setNewWidgetTitle(''); setNewWidgetCode(block.customCode || ''); setShowCreateModal(true); }}>💾 Enregistrer comme modèle</button>
                                        )}
                                    </div>
                                </div>
                                <p style={{ fontSize: '0.8rem', opacity: 0.7, marginBottom: '0.5rem', color: isCustomFullscreen ? '#aaa' : 'inherit' }}>
                                    Utilisez <code>render(&lt;MonComposant /&gt;);</code> à la fin de votre code.
                                </p>
                                <textarea
                                    rows={isCustomFullscreen ? "30" : "10"}
                                    style={{
                                        fontFamily: 'monospace',
                                        fontSize: '0.85rem',
                                        background: '#1e1e1e',
                                        color: '#d4d4d4',
                                        padding: '1rem',
                                        borderRadius: '8px',
                                        flex: isCustomFullscreen ? 1 : 'unset'
                                    }}
                                    value={block.customCode || ''}
                                    onChange={e => updateBlock(index, { customCode: e.target.value })}
                                />
                            </div>
                        )}
                    </div>
                )
                }

                {block.type === 'html' && (
                    <div style={{ display: 'flex', flexDirection: 'column', gap: '1.5rem', position: 'relative' }}>
                        <div className="admin-form-row">
                            <div className="admin-input-group" style={{ flex: 1 }}>
                                <label>Mode d'intégration</label>
                                <select value={block.mode || 'custom'} onChange={e => updateBlock(index, { mode: e.target.value })}>
                                    <option value="custom">✍️ Code HTML unique</option>
                                    <option value="library">📚 Bibliothèque HTML</option>
                                </select>
                            </div>
                            <div className="admin-input-group" style={{ width: '180px' }}>
                                <label>Conteneur</label>
                                <select value={block.containerType || 'div'} onChange={e => updateBlock(index, { containerType: e.target.value })}>
                                    <option value="div">Standard (div)</option>
                                    <option value="iframe">Isolé (iframe)</option>
                                    <option value="none">Aucun (Fragment)</option>
                                </select>
                            </div>
                            <div className="admin-input-group" style={{ width: '150px' }}>
                                <label>Thème</label>
                                <select value={block.themeOverride || 'auto'} onChange={e => updateBlock(index, { themeOverride: e.target.value })}>
                                    <option value="auto">🌓 Auto</option>
                                    <option value="light">☀️ Clair</option>
                                    <option value="dark">🌙 Sombre</option>
                                </select>
                            </div>
                        </div>

                        {block.mode === 'library' ? (
                            <div className="admin-input-group">
                                <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: '0.5rem' }}>
                                    <label style={{ margin: 0 }}>Choisir un composant HTML</label>
                                    <div style={{ display: 'flex', gap: '4px' }}>
                                        <button type="button" className="btn btn-xs btn-outline" onClick={onOpenLibrary}>📚 Bibliothèque</button>
                                        <button type="button" className="btn btn-xs btn-outline" onClick={() => { setNewWidgetTitle(''); setNewWidgetCode(''); setEditingWidgetId(null); setShowCreateModal(true); }}>➕ Nouveau</button>
                                    </div>
                                </div>
                                <div style={{ display: 'flex', gap: '0.5rem' }}>
                                    <select value={block.htmlId || ''} onChange={e => updateBlock(index, { htmlId: e.target.value })} style={{ flex: 1 }}>
                                        <option value="">-- Sélectionner --</option>
                                        {(availableWidgets || []).filter(w => w.type === 'html').map(w => (
                                            <option key={w.id} value={w.id}>{w.title}</option>
                                        ))}
                                    </select>
                                    {block.htmlId && (
                                        <div style={{ display: 'flex', gap: '4px' }}>
                                            <button type="button" className="btn btn-xs btn-outline" onClick={() => {
                                                const w = availableWidgets.find(x => x.id === block.htmlId);
                                                if (w) { setNewWidgetTitle(w.title); setNewWidgetCode(w.code); setEditingWidgetId(w.id); setShowCreateModal(true); }
                                            }}>✏️</button>
                                            <button type="button" className="btn btn-xs btn-danger" onClick={async () => {
                                                if (window.confirm("Supprimer ce composant HTML de la bibliothèque ?")) {
                                                    try {
                                                        await pb.collection('custom_widgets').delete(block.htmlId);
                                                        await refreshWidgets();
                                                        updateBlock(index, { htmlId: '' });
                                                    } catch (err) { alert(err.message); }
                                                }
                                            }}>🗑️</button>
                                        </div>
                                    )}
                                </div>
                            </div>
                        ) : (
                            <div className="admin-input-group" style={isHtmlFullscreen ? {
                                position: 'fixed', inset: 0, zIndex: 11000, background: '#1e1e1e', padding: '2rem', display: 'flex', flexDirection: 'column', margin: 0
                            } : {}}>
                                <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: '0.5rem' }}>
                                    <label style={isHtmlFullscreen ? { color: 'white' } : { margin: 0 }}>Code HTML / CSS / JS</label>
                                    <div style={{ display: 'flex', gap: '8px' }}>
                                        <button type="button" className="btn btn-xs" onClick={() => setIsHtmlFullscreen(!isHtmlFullscreen)} style={isHtmlFullscreen ? { color: 'white', borderColor: 'rgba(255,255,255,0.4)', background: 'rgba(255,255,255,0.15)' } : {}}>
                                            {isHtmlFullscreen ? '↙ Quitter' : '📂 Plein écran'}
                                        </button>
                                        {!isHtmlFullscreen && (
                                            <button type="button" className="btn btn-xs btn-secondary" onClick={() => handleSaveToLibrary('Nouveau Composant HTML', block.htmlCode, 'html')}>💾 Sauver en modèle</button>
                                        )}
                                    </div>
                                </div>
                                <textarea
                                    rows={isHtmlFullscreen ? "30" : "10"}
                                    style={{ fontFamily: 'monospace', fontSize: '0.85rem', background: '#1e1e1e', color: '#d4d4d4', padding: '1rem', borderRadius: '8px', flex: isHtmlFullscreen ? 1 : 'unset' }}
                                    value={block.htmlCode || ''}
                                    onChange={e => updateBlock(index, { htmlCode: e.target.value })}
                                />
                            </div>
                        )}
                    </div>
                )}
            </div>

            {/* TOAST NOTIFICATION */}
            {toast.show && (
                <div style={{
                    position: 'fixed',
                    bottom: '30px',
                    right: '30px',
                    background: 'linear-gradient(135deg, #10b981 0%, #059669 100%)',
                    color: 'white',
                    padding: '12px 24px',
                    borderRadius: '16px',
                    boxShadow: '0 10px 30px rgba(0,0,0,0.2)',
                    zIndex: 100000,
                    fontWeight: 'bold',
                    display: 'flex',
                    alignItems: 'center',
                    gap: '10px',
                    animation: 'adminToastIn 0.4s cubic-bezier(0.175, 0.885, 0.32, 1.275)'
                }}>
                    <span>✅</span> {toast.message}
                </div>
            )}
        </div>
    );
}

const BlockListEditor = ({ blocks, onChange, variant = 'nested' }) => {

    const bottomRef = React.useRef(null);
    const prevCount = React.useRef(blocks?.length || 0);
    const [availableWidgets, setAvailableWidgets] = React.useState([]);
    const [showLibraryManager, setShowLibraryManager] = React.useState(false);
    const [searchTerm, setSearchTerm] = React.useState('');
    const [isManagerFullscreen, setIsManagerFullscreen] = React.useState(false);
    const [libraryTab, setLibraryTab] = React.useState('all');

    // State for editing within the manager
    const [managerEditingWidget, setManagerEditingWidget] = React.useState(null);
    const [managerIsSaving, setManagerIsSaving] = React.useState(false);
    const [toast, setToast] = React.useState({ show: false, message: '' });

    const loadWidgets = async () => {
        try {
            const records = await pb.collection('custom_widgets').getFullList({ sort: 'title' });
            setAvailableWidgets(records);
        } catch (err) {
            console.warn("Failed to load widgets library", err);
        }
    };

    // Load custom widgets library
    React.useEffect(() => {
        loadWidgets();
    }, []);

    // Ensure every block has a unique ID for DnD stability
    const normalizedBlocks = useMemo(() => {
        return blocks.map((b, idx) => {
            if (b.id) return b;
            return { ...b, id: `block-${Date.now()}-${idx}` };
        });
    }, [blocks]);

    // Auto-scroll logic when a block is added
    React.useEffect(() => {
        if (blocks?.length > prevCount.current) {
            setTimeout(() => {
                bottomRef.current?.scrollIntoView({ behavior: 'smooth', block: 'end' });
            }, 100);
        }
        prevCount.current = blocks?.length || 0;
    }, [blocks?.length]);

    const sensors = useSensors(
        useSensor(PointerSensor, { activationConstraint: { distance: 5 } }),
        useSensor(KeyboardSensor, { coordinateGetter: sortableKeyboardCoordinates })
    );

    const onDragEnd = (event) => {
        const { active, over } = event;
        if (!over || active.id === over.id) return;

        const oldIndex = normalizedBlocks.findIndex(b => b.id === active.id);
        const newIndex = normalizedBlocks.findIndex(b => b.id === over.id);

        onChange(arrayMove(normalizedBlocks, oldIndex, newIndex));
    };

    const [confirmDelete, setConfirmDelete] = useState({ show: false, index: null });

    const updateBlock = (index, payload) => {
        const newBlocks = [...normalizedBlocks];
        newBlocks[index] = { ...newBlocks[index], ...payload };
        onChange(newBlocks);
    };

    const deleteBlock = (index) => {
        setConfirmDelete({ show: true, index });
    };

    const confirmDeletion = () => {
        if (confirmDelete.index === null) return;
        const newBlocks = [...normalizedBlocks];
        newBlocks.splice(confirmDelete.index, 1);
        onChange(newBlocks);
        setConfirmDelete({ show: false, index: null });
    };

    const duplicateBlock = (index) => {
        const newBlocks = [...normalizedBlocks];
        const copy = JSON.parse(JSON.stringify(newBlocks[index]));
        copy.id = `block-${Date.now()}-copy`;
        newBlocks.splice(index + 1, 0, copy);
        onChange(newBlocks);
        setToast({ show: true, message: "Bloc dupliqué !" });
        setTimeout(() => setToast({ show: false, message: '' }), 3000);
    };

    const addBlock = (type, defaults = {}) => {
        const newBlock = { type, id: `block-${Date.now()}`, ...defaults };
        if (type === 'text' && !newBlock.content) newBlock.content = '';
        if (type === 'button' && !newBlock.label) { newBlock.label = 'En savoir plus'; newBlock.url = '/'; newBlock.style = 'btn-secondary'; }
        if (type === 'image' && !newBlock.url) { newBlock.url = ''; newBlock.caption = ''; newBlock.align = 'center'; newBlock.rounded = false; }
        if (type === 'accordion' && !newBlock.items) { newBlock.items = [{ title: 'Titre volet', content: '' }]; }
        if (type === 'testimonial' && !newBlock.quote) { newBlock.quote = ''; newBlock.author = ''; newBlock.role = ''; newBlock.avatar = ''; }
        if (type === 'list' && !newBlock.items) { newBlock.items = ['']; newBlock.style = 'check'; }
        if (type === 'callout') {
            if (!newBlock.style) newBlock.style = 'info';
            if (!newBlock.content) newBlock.content = '';
        }
        if (type === 'video' && !newBlock.url) { newBlock.url = ''; }
        if (type === 'timeline' && !newBlock.items) { newBlock.items = [{ title: 'Entretien RH', content: '' }]; }
        if (type === 'stats' && !newBlock.items) { newBlock.items = [{ label: 'Consultants', value: '50' }]; }
        if (type === 'perks' && !newBlock.items) { newBlock.items = [{ title: 'Télétravail', icon: '🏠' }]; }
        if (type === 'downloads' && !newBlock.items) { newBlock.items = [{ label: 'Livret d\'accueil', url: '', size: '2 MB' }]; }
        if (type === 'team' && !newBlock.members) { newBlock.members = [{ name: 'Prénom Nom', role: 'Dev Fullstack', avatar: '', bio: '', socials: [] }]; }
        if (type === 'map' && !newBlock.address) { newBlock.address = 'Versailles, France'; newBlock.zoom = 13; }
        if (type === 'stack' && !newBlock.items) { newBlock.items = ['React', 'Node.js']; }
        if (type === 'spacer' && !newBlock.height) { newBlock.height = '40px'; newBlock.showLine = false; }
        if (type === 'gallery' && !newBlock.images) { newBlock.images = []; newBlock.layout = 'grid'; }
        if (type === 'highlight' && !newBlock.text) { newBlock.text = 'Information importante'; newBlock.style = 'gradient'; }
        if (type === 'sidebyside' && !newBlock.text) {
            newBlock.text = 'Description...';
            newBlock.mediaUrl = '';
            newBlock.mediaType = 'image';
            newBlock.imageRight = true;
        }
        if (type === 'tabs' && !newBlock.items) { newBlock.items = [{ title: 'Onglet 1', content: '' }]; }
        if (type === 'beforeafter' && !newBlock.beforeUrl) { newBlock.beforeUrl = ''; newBlock.afterUrl = ''; }
        if (type === 'countdown' && !newBlock.endDate) { newBlock.endDate = ''; newBlock.label = 'Fin de l\'offre dans :'; }
        if (type === 'marquee' && !newBlock.images) { newBlock.images = []; newBlock.speed = 20; }
        if (type === 'tooltip' && !newBlock.text) { newBlock.text = 'Mon texte'; newBlock.tooltip = 'Explication...'; }
        if (type === 'codesnippet' && !newBlock.code) { newBlock.code = 'console.log("Hello World");'; newBlock.language = 'javascript'; }
        if (type === 'awards' && !newBlock.items) { newBlock.items = [{ title: 'Certification', image: '' }]; }
        if (type === 'widget' && !newBlock.mode) {
            newBlock.mode = 'library'; // or 'custom'
            newBlock.widgetId = '';
            newBlock.customCode = 'const MyPremiumCard = () => {\n  return (\n    <div style={{\n      padding: "40px",\n      background: "linear-gradient(135deg, #6366f1 0%, #a855f7 100%)",\n      borderRadius: "24px",\n      color: "white",\n      boxShadow: "0 20px 40px rgba(99, 102, 241, 0.3)",\n      textAlign: "center",\n      margin: "20px 0"\n    }}>\n      <h2 style={{ fontSize: "2rem", marginBottom: "10px", color: "white" }}>🚀 Prêt à nous rejoindre ?</h2>\n      <p style={{ opacity: 0.9, fontSize: "1.1rem" }}>Découvrez nos opportunités et construisez votre avenir avec nous.</p>\n      <button style={{\n        marginTop: "20px",\n        background: "white",\n        color: "#6366f1",\n        border: "none",\n        padding: "12px 30px",\n        borderRadius: "12px",\n        fontWeight: "bold",\n        cursor: "pointer",\n        transition: "transform 0.2s"\n      }}>Postuler maintenant</button>\n    </div>\n  );\n};\n\nrender(<MyPremiumCard />);';
        }
        if (type === 'html' && !newBlock.htmlCode) {
            newBlock.htmlCode = '<div class="custom-html-block">\n  <h2>Mon Titre HTML</h2>\n  <p>Mon contenu personnalisé...</p>\n</div>\n\n<style>\n.custom-html-block {\n  padding: 2rem;\n  border-radius: 12px;\n  background: #f8fafc;\n}\n</style>';
            newBlock.mode = 'custom';
            newBlock.containerType = 'div';
        }

        if (type === 'grid' && !newBlock.columns) {
            newBlock.columnsCount = 1;
            newBlock.columns = [[]];
        }
        onChange([...normalizedBlocks, newBlock]);
    };

    const updateGridColumn = (blockIndex, colIndex, newColBlocks) => {
        const newBlocks = [...normalizedBlocks];
        const gridBlock = { ...newBlocks[blockIndex] };
        const newColumns = [...gridBlock.columns];
        newColumns[colIndex] = newColBlocks;
        gridBlock.columns = newColumns;
        newBlocks[blockIndex] = gridBlock;
        onChange(newBlocks);
    };

    const updateGridConfig = (blockIndex, count) => {
        const newBlocks = [...normalizedBlocks];
        const gridBlock = { ...newBlocks[blockIndex] };
        gridBlock.columnsCount = count;
        const currentCols = [...(gridBlock.columns || [])];
        if (count > currentCols.length) {
            for (let i = currentCols.length; i < count; i++) currentCols.push([]);
        } else if (count < currentCols.length) {
            currentCols.splice(count);
        }
        gridBlock.columns = currentCols;
        newBlocks[blockIndex] = gridBlock;
        onChange(newBlocks);
    };

    return (
        <div className="blocks-editor">
            {/* BUILDER TOOLBAR */}
            <div className="block-builder-toolbar" style={{ display: 'flex', gap: '0.75rem', marginBottom: '1.5rem' }}>
                {variant === 'root' ? (
                    <>
                        <button type="button" className="btn btn-primary" onClick={() => addBlock('grid')} style={{ flex: 1, padding: '12px', fontWeight: 'bold' }}>
                            + Ajouter une ligne de contenu (Grille)
                        </button>
                        <button type="button" className="btn btn-secondary" onClick={() => setShowLibraryManager(true)} style={{ padding: '0 1.5rem', fontWeight: 'bold' }}>
                            📚 Bibliothèque
                        </button>
                    </>
                ) : (
                    <>
                        <button type="button" className="btn btn-xs btn-outline" onClick={() => addBlock('grid')}>🏗️ Grille</button>
                        <button type="button" className="btn btn-xs btn-outline" onClick={() => addBlock('text')}>📝 Texte</button>
                        <button type="button" className="btn btn-xs btn-outline" onClick={() => addBlock('image')}>🖼️ Image</button>
                        <button type="button" className="btn btn-xs btn-outline" onClick={() => addBlock('video')}>🎥 Vidéo</button>
                        <button type="button" className="btn btn-xs btn-outline" onClick={() => addBlock('button')}>🔘 Bouton</button>
                        <button type="button" className="btn btn-xs btn-outline" onClick={() => addBlock('accordion')}>🔽 Accordéon</button>
                        <button type="button" className="btn btn-xs btn-outline" onClick={() => addBlock('testimonial')}>⭐ Citation</button>
                        <button type="button" className="btn btn-xs btn-outline" onClick={() => addBlock('list')}>📊 Liste</button>
                        <button type="button" className="btn btn-xs btn-outline" onClick={() => addBlock('callout')}>🔔 Note</button>
                        <button type="button" className="btn btn-xs btn-outline" onClick={() => addBlock('timeline')}>🛣️ Processus</button>
                        <button type="button" className="btn btn-xs btn-outline" onClick={() => addBlock('stats')}>🔢 Stats</button>
                        <button type="button" className="btn btn-xs btn-outline" onClick={() => addBlock('perks')}>🛡️ Perks</button>
                        <button type="button" className="btn btn-xs btn-outline" onClick={() => addBlock('downloads')}>📂 Fichiers</button>
                        <button type="button" className="btn btn-xs btn-outline" onClick={() => addBlock('team')}>👥 Équipe</button>
                        <button type="button" className="btn btn-xs btn-outline" onClick={() => addBlock('map')}>🗺️ Carte</button>
                        <button type="button" className="btn btn-xs btn-outline" onClick={() => addBlock('stack')}>💻 Stack</button>
                        <button type="button" className="btn btn-xs btn-outline" onClick={() => addBlock('spacer')}>↔️ Espaceur</button>
                        <button type="button" className="btn btn-xs btn-outline" onClick={() => addBlock('gallery')}>🏢 Galerie</button>
                        <button type="button" className="btn btn-xs btn-outline" onClick={() => addBlock('highlight')}>🚨 Highlight</button>
                        <button type="button" className="btn btn-xs btn-outline" onClick={() => addBlock('sidebyside')}>💎 Média+Texte</button>
                        <button type="button" className="btn btn-xs btn-outline" onClick={() => addBlock('tabs')}>🗂️ Onglets</button>
                        <button type="button" className="btn btn-xs btn-outline" onClick={() => addBlock('beforeafter')}>🔀 Avant/Après</button>
                        <button type="button" className="btn btn-xs btn-outline" onClick={() => addBlock('countdown')}>⏳ Countdown</button>
                        <button type="button" className="btn btn-xs btn-outline" onClick={() => addBlock('marquee')}>🛡️ Marquee</button>
                        <button type="button" className="btn btn-xs btn-outline" onClick={() => addBlock('tooltip')}>💡 Info-bulle</button>
                        <button type="button" className="btn btn-xs btn-outline" onClick={() => addBlock('codesnippet')}>💻 Code</button>
                        <button type="button" className="btn btn-xs btn-outline" onClick={() => addBlock('awards')}>🏆 Certifs</button>
                        <button type="button" className="btn btn-xs btn-outline" onClick={() => addBlock('html')} style={{ borderColor: '#f59e0b', color: '#f59e0b' }}>🌐 Code HTML</button>
                        <button type="button" className="btn btn-xs btn-outline" onClick={() => addBlock('widget')} style={{ borderColor: 'var(--admin-primary)', color: 'var(--admin-primary)' }}>🔌 Super-Widget</button>
                    </>
                )}
            </div>

            <DndContext sensors={sensors} collisionDetection={closestCenter} onDragEnd={onDragEnd}>
                <SortableContext items={normalizedBlocks.map(b => b.id)} strategy={verticalListSortingStrategy}>
                    <div style={{ display: 'flex', flexDirection: 'column', gap: '1.5rem' }}>
                        {normalizedBlocks.map((block, idx) => (
                            <SortableBlockItem
                                key={block.id}
                                block={block}
                                index={idx}
                                updateBlock={updateBlock}
                                deleteBlock={deleteBlock}
                                updateGridColumn={updateGridColumn}
                                updateGridConfig={updateGridConfig}
                                BlockListEditorComponent={BlockListEditor}
                                availableWidgets={availableWidgets}
                                refreshWidgets={loadWidgets}
                                onOpenLibrary={() => setShowLibraryManager(true)}
                                duplicateBlock={duplicateBlock}
                            />
                        ))}
                        {normalizedBlocks.length === 0 && (
                            <div className="empty-state">
                                <div style={{ fontSize: '2.5rem', opacity: 0.5 }}>🏗️</div>
                                <div>
                                    <p style={{ margin: 0, fontWeight: 600 }}>Aucun contenu pour le moment</p>
                                    <p className="text-muted" style={{ margin: 0, fontSize: '0.9rem' }}>Utilisez la barre d'outils ci-dessus pour construire votre mise en page.</p>
                                </div>
                            </div>
                        )}
                        <div ref={bottomRef} style={{ height: '1px' }} />
                    </div>
                </SortableContext>
            </DndContext>

            {/* LIBRARY MANAGER MODAL */}
            {showLibraryManager && (
                <div className="admin-modal-overlay">
                    <div className="admin-modal" style={{ width: '1000px', maxWidth: '95vw', maxHeight: '90vh', display: 'flex', flexDirection: 'column' }}>
                        <div className="admin-modal-header" style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
                            <h3 style={{ margin: 0 }}>📚 Gestionnaire de Bibliothèque</h3>
                            <button className="btn-icon" onClick={() => setShowLibraryManager(false)}>✕</button>
                        </div>

                        <div className="admin-modal-body" style={{ flex: 1, overflowY: 'auto', padding: '1.5rem' }}>
                            {managerEditingWidget ? (
                                <div style={{ background: 'var(--admin-hover-bg)', padding: '1.5rem', borderRadius: '16px', border: '1px solid var(--admin-border)' }}>
                                    <h4 style={{ marginTop: 0 }}>✏️ Modification : {managerEditingWidget.title}</h4>
                                    <div className="admin-input-group">
                                        <label>Titre</label>
                                        <input
                                            value={managerEditingWidget.title}
                                            onChange={e => setManagerEditingWidget({ ...managerEditingWidget, title: e.target.value })}
                                        />
                                    </div>
                                    <div className="admin-input-group" style={isManagerFullscreen ? {
                                        position: 'fixed',
                                        inset: 0,
                                        zIndex: 11000,
                                        background: '#1e1e1e',
                                        padding: '2rem',
                                        display: 'flex',
                                        flexDirection: 'column',
                                        margin: 0
                                    } : { marginTop: '1.5rem' }}>
                                        <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: '0.5rem' }}>
                                            <label style={isManagerFullscreen ? { color: 'white' } : {}}>Code React / JSX</label>
                                            <button
                                                type="button"
                                                className="btn btn-xs"
                                                onClick={() => setIsManagerFullscreen(!isManagerFullscreen)}
                                                style={isManagerFullscreen ? { color: 'white', borderColor: 'rgba(255,255,255,0.4)', background: 'rgba(255,255,255,0.15)' } : {}}
                                            >
                                                {isManagerFullscreen ? '↙ Quitter le plein écran' : '📂 Plein écran'}
                                            </button>
                                        </div>
                                        <textarea
                                            rows={isManagerFullscreen ? "30" : "12"}
                                            style={{
                                                fontFamily: 'monospace',
                                                fontSize: '0.85rem',
                                                background: '#1e1e1e',
                                                color: '#d4d4d4',
                                                flex: isManagerFullscreen ? 1 : 'unset'
                                            }}
                                            value={managerEditingWidget.code}
                                            onChange={e => setManagerEditingWidget({ ...managerEditingWidget, code: e.target.value })}
                                        />
                                    </div>
                                    <div style={{ display: 'flex', gap: '1rem', marginTop: '1.5rem' }}>
                                        <button type="button" className="btn btn-ghost" onClick={() => setManagerEditingWidget(null)}>Annuler</button>
                                        <button type="button" className="btn btn-primary" onClick={async () => {
                                            setManagerIsSaving(true);
                                            try {
                                                await pb.collection('custom_widgets').update(managerEditingWidget.id, {
                                                    title: managerEditingWidget.title,
                                                    code: managerEditingWidget.code
                                                });
                                                await loadWidgets();
                                                setManagerEditingWidget(null);
                                                setToast({ show: true, message: "Composant mis à jour !" });
                                                setTimeout(() => setToast({ show: false, message: '' }), 3000);
                                            } catch (err) { alert(err.message); }
                                            finally { setManagerIsSaving(false); }
                                        }} disabled={managerIsSaving}>
                                            {managerIsSaving ? 'Enregistrement...' : '💾 Sauvegarder les changements'}
                                        </button>
                                    </div>
                                </div>
                            ) : (
                                <>
                                    <div style={{ display: 'flex', gap: '1rem', marginBottom: '1.5rem' }}>
                                        <button type="button" className={`btn btn-sm ${!searchTerm && libraryTab === 'all' ? 'btn-primary' : 'btn-outline'}`} onClick={() => { setLibraryTab('all'); setSearchTerm(''); }}>Tout</button>
                                        <button type="button" className={`btn btn-sm ${libraryTab === 'react' ? 'btn-primary' : 'btn-outline'}`} onClick={() => setLibraryTab('react')}>🔌 React</button>
                                        <button type="button" className={`btn btn-sm ${libraryTab === 'html' ? 'btn-primary' : 'btn-outline'}`} onClick={() => setLibraryTab('html')}>🌐 HTML</button>
                                        <div style={{ flex: 1 }}></div>
                                        <input
                                            placeholder="🔍 Rechercher..."
                                            value={searchTerm}
                                            onChange={e => setSearchTerm(e.target.value)}
                                            style={{ padding: '0.5rem 1rem', borderRadius: '10px', width: '250px' }}
                                        />
                                    </div>

                                    <div style={{ display: 'grid', gap: '1rem' }}>
                                        {availableWidgets
                                            .filter(w => (libraryTab === 'all' || w.type === libraryTab) && w.title.toLowerCase().includes(searchTerm.toLowerCase()))
                                            .map(widget => (
                                                <div key={widget.id} style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', background: 'var(--admin-hover-bg)', padding: '1rem', borderRadius: '12px', border: '1px solid var(--admin-border)' }}>
                                                    <div>
                                                        <div style={{ fontWeight: 'bold' }}>{widget.title}</div>
                                                        <div style={{ fontSize: '0.7rem', color: 'var(--text-muted)' }}>ID: {widget.id} • Type: {widget.type}</div>
                                                    </div>
                                                    <div style={{ display: 'flex', gap: '8px' }}>
                                                        <button type="button" className="btn btn-xs btn-outline" onClick={() => setManagerEditingWidget(widget)}>Modifier</button>
                                                        <button type="button" className="btn btn-xs btn-secondary" onClick={async () => {
                                                            try {
                                                                const newWidget = {
                                                                    title: `${widget.title} (Copie)`,
                                                                    code: widget.code,
                                                                    type: widget.type,
                                                                    css: widget.css || '',
                                                                    config: widget.config || {}
                                                                };
                                                                await pb.collection('custom_widgets').create(newWidget);
                                                                await loadWidgets();
                                                                setToast({ show: true, message: "Composant dupliqué !" });
                                                                setTimeout(() => setToast({ show: false, message: '' }), 3000);
                                                            } catch (err) { alert(err.message); }
                                                        }}>Dupliquer</button>
                                                        <button type="button" className="btn btn-xs btn-danger" onClick={async () => {
                                                            if (window.confirm(`Supprimer "${widget.title}" ? Cette action est irréversible et affectera toutes les pages l'utilisant.`)) {
                                                                try {
                                                                    await pb.collection('custom_widgets').delete(widget.id);
                                                                    await loadWidgets();
                                                                } catch (err) { alert(err.message); }
                                                            }
                                                        }}>Supprimer</button>
                                                    </div>
                                                </div>
                                            ))}
                                        {availableWidgets.length === 0 && <p style={{ textAlign: 'center', opacity: 0.5 }}>Votre bibliothèque est vide.</p>}
                                    </div>
                                </>
                            )}
                        </div>

                        <div className="admin-modal-footer">
                            <button type="button" className="btn btn-primary" onClick={() => setShowLibraryManager(false)}>Fermer</button>
                        </div>
                    </div>
                </div>
            )}

            {/* CUSTOM DELETE CONFIRMATION MODAL */}
            {confirmDelete.show && (
                <div className="admin-modal-overlay">
                    <div className="admin-modal">
                        <div className="admin-modal-header">
                            <h3>⚠️ Supprimer ce bloc ?</h3>
                        </div>
                        <div className="admin-modal-body">
                            <p>Cette action est irréversible. Toutes les données de ce bloc seront perdues.</p>
                        </div>
                        <div className="admin-modal-footer">
                            <button type="button" className="btn btn-ghost" onClick={() => setConfirmDelete({ show: false, index: null })}>Annuler</button>
                            <button type="button" className="btn btn-danger" onClick={confirmDeletion}>Supprimer définitivement</button>
                        </div>
                    </div>
                </div>
            )}

            {/* TOAST NOTIFICATION */}
            {toast.show && (
                <div style={{
                    position: 'fixed',
                    bottom: '30px',
                    right: '30px',
                    background: 'linear-gradient(135deg, #10b981 0%, #059669 100%)',
                    color: 'white',
                    padding: '12px 24px',
                    borderRadius: '16px',
                    boxShadow: '0 10px 30px rgba(0,0,0,0.2)',
                    zIndex: 100000,
                    fontWeight: 'bold',
                    display: 'flex',
                    alignItems: 'center',
                    gap: '10px',
                    animation: 'adminToastIn 0.4s cubic-bezier(0.175, 0.885, 0.32, 1.275)'
                }}>
                    <span>✅</span> {toast.message}
                </div>
            )}
        </div>
    );
};

export default BlockListEditor;
